 // src/Dashboard.jsx
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useAuth } from "../context/AuthContext";
import {
  Card, Space, Typography, Button, message, Input, Grid, Badge, Tooltip, Avatar,
  Collapse, Segmented, theme, Layout, Menu, ConfigProvider,
  Spin, Modal, Drawer, FloatButton
} from 'antd';
import {
  ThunderboltFilled, ShareAltOutlined, ReloadOutlined,
  UserOutlined, GiftFilled,
  DashboardOutlined, SettingOutlined, QuestionCircleOutlined, LogoutOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined, SafetyCertificateFilled, MenuOutlined,
  ReadOutlined
} from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';

import {
  getAuth,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword as fbUpdatePassword,
  deleteUser,
} from 'firebase/auth';

// util viewport zoom
const originalViewportRef = { current: null };
const lockZoom = () => {
  const vp = document.querySelector('meta[name="viewport"]');
  if (!vp) return;
  if (!originalViewportRef.current) originalViewportRef.current = vp.getAttribute('content');
  vp.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
};
const unlockZoom = () => {
  const vp = document.querySelector('meta[name="viewport"]');
  if (!vp) return;
  vp.setAttribute('content', originalViewportRef.current || 'width=device-width, initial-scale=1');
  originalViewportRef.current = null;
};

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;
const { useToken } = theme;
const { Sider, Header, Content } = Layout;



const APP_CONFIG = {
  BRAND: {
    siderBg: '#111318',
    headerBg: '#111318',
    layoutBg: '#0c0e13',
    cardBg: '#151823',
    primary: '#6c8cff',
    gradient: {
      primary: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      dark: 'linear-gradient(135deg, #111827 0%, #1f2937 50%, #312e81 100%)'
    }
  },
  URLs: {
    broker: "https://my.x-fine.com/links/go/257",
    baseReferral: "https://records.axiaffiliates.com/visit/?bta=40304&brand=axitradersub",
    Android:"https://play.google.com/store/apps/details?id=cn.com.vpu&pcampaignid=web_share",
   Ios:"https://apps.apple.com/au/app/pu-prime-trading-app/id1575499773"
  
  },
  SUPPORT: {
    email: "support@sweet-trading.com",
    telegram: "https://t.me/assistenceST"
  },
  SECRET_CODE: "BEIVSLA61HVSK620HVSK"
};

/* === HTML guida STANDARD per iframe === */
const GUIDE_HTML_STANDARD = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1"/>
  <title>Candy Bot Standard Guide - Sweet Trading</title>
  <script src="https://cdn.tailwindcss.com"></script>
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css"/>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
    :root { --primary:#6366f1; --secondary:#8b5cf6; --accent:#ec4899; }
    body {
      font-family:'Inter',sans-serif;
      background:linear-gradient(135deg,#0f172a 0%,#1e293b 50%,#1e1b4b 100%);
      color:#e2e8f0;
    }
    .header-gradient {
      background:linear-gradient(135deg,rgba(15,23,42,.95) 0%,rgba(30,41,59,.95) 100%);
      backdrop-filter:blur(10px);
    }
    .step-number {
      background:linear-gradient(135deg,var(--primary) 0%,var(--secondary) 100%);
      box-shadow:0 4px 15px rgba(99,102,241,.3);
    }
    .step-card {
      background:rgba(15,23,42,.7);
      backdrop-filter:blur(8px);
      border:1px solid rgba(99,102,241,.2);
      transition:all .3s ease;
    }
    .step-card:hover {
      border-color:rgba(99,102,241,.4);
      transform:translateY(-2px);
      box-shadow:0 10px 25px -5px rgba(99,102,241,.1);
    }
    .primary-btn {
      background:linear-gradient(135deg,var(--primary) 0%,var(--secondary) 100%);
      transition:all .3s ease;
    }
    .primary-btn:hover {
      transform:translateY(-2px);
      box-shadow:0 10px 20px -5px rgba(99,102,241,.4);
    }
    .info-bubble { background:rgba(236,72,153,.1); border-left:4px solid var(--accent); }
    .tip-bubble { background:rgba(59,130,246,.1); border-left:4px solid #3b82f6; }
    .animate-float { animation:float 3s ease-in-out infinite; }
    @keyframes float { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-5px); } }
  </style>
</head>
<body class="min-h-screen">
<header class="header-gradient w-full flex justify-between items-center px-6 py-4 sticky top-0 z-50 border-b border-slate-700/50">
  <div class="flex items-center gap-4">
    <div class="flex items-center">
      <img src="/logo1-st.png" alt="Sweet Trading Logo" class="h-12 w-auto animate-float"/>
    </div>
  </div>
  <div class="flex items-center gap-4">
    <select id="language-selector" class="bg-slate-800/50 text-white border border-slate-600 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
      <option value="en">🇬🇧 English</option>
      <option value="it">🇮🇹 Italiano</option>
    </select>
  </div>
</header>

<section class="relative py-20 px-6 overflow-hidden">
  <div class="absolute inset-0 opacity-20">
    <div class="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-indigo-900/50 to-transparent"></div>
    <div class="absolute bottom-0 right-0 w-1/3 h-full bg-gradient-to-l from-purple-900/50 to-transparent"></div>
  </div>
  <div class="relative max-w-4xl mx-auto text-center">
    <h1 id="header-title" class="text-4xl md:text-5xl font-bold mb-6 text-white">
      <span class="bg-clip-text text-transparent bg-gradient-to-r from-indigo-300 to-pink-400">
        Candy Bot Standard Guide
      </span>
    </h1>
    <p id="header-subtitle" class="text-xl text-indigo-300 max-w-2xl mx-auto">
      Step-by-step setup to maximize your bot's performance
    </p>
    <div class="mt-10 flex justify-center gap-4">
      <a href="#step-1" id="get-started" class="primary-btn text-white font-medium py-3 px-6 rounded-lg inline-flex items-center">
        Get Started <i class="fas fa-arrow-down ml-2"></i>
      </a>
    </div>
  </div>
</section>

<div class="container mx-auto px-4 py-12 max-w-4xl">
  <!-- STEP 1 -->
  <div id="step-1" class="step-card rounded-xl p-4 md:p-8 mb-8">
    <div class="flex items-start mb-6">
      <div class="step-number rounded-full w-10 h-10 md:w-12 md:h-12 flex items-center justify-center text-white font-bold text-lg md:text-lg mr-3 md:mr-4 flex-shrink-0">1</div>
      <div>
        <h2 id="step-1a-title" class="text-xl md:text-2xl font-bold text-white mb-2">
          Create Your Trading Account 📊
        </h2>
        <div class="flex flex-wrap gap-2 mb-4">
          <span class="bg-indigo-900/50 text-indigo-200 text-xs font-medium px-2.5 py-0.5 rounded">First Step</span>
          <span class="bg-pink-900/50 text-pink-200 text-xs font-medium px-2.5 py-0.5 rounded">Important</span>
        </div>
      </div>
    </div>
    <div class="ml-0 md:ml-16 pl-0 md:pl-2">
      <div class="prose prose-invert max-w-none">
        <p id="step-1a-desc" class="mb-4">
          Register on the broker's website using the button below:
        </p>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <a
            href="${APP_CONFIG.URLs.broker}"
            target="_blank"
            rel="noopener noreferrer"
            id="go-to-broker"
            class="primary-btn text-white font-medium py-3 px-6 rounded-lg text-center"
          >
            Go to Broker
          </a>
        </div>
        <div class="info-bubble p-4 rounded-lg mb-6">
          <p id="pro-tip" class="text-pink-200">
            <i class="fas fa-lightbulb mr-2"></i>
            <strong>Pro Tip:</strong> Keep two browser tabs open - one for this guide and one for registration.
          </p>
          
        </div>
       


      </div>
    </div>
  </div>

  <!-- STEP 2 -->
  <div class="step-card rounded-xl p-4 md:p-8 mb-8">
    <div class="flex items-start mb-6">
      <div class="step-number rounded-full w-10 h-10 md:w-12 md:h-12 flex items-center justify-center text-white font-bold text-lg md:text-lg mr-3 md:mr-4 flex-shrink-0">2</div>
      <h2 id="step-1b-title" class="text-xl md:text-2xl font-bold text-white">
        Account creation
      </h2>
    </div>
    <div class="ml-0 md:ml-16 pl-0 md:pl-2">
      <p id="step-1b-desc" class="mb-4">
        On the page that appears:
      </p>
      <ul id="step-1b-list" class="list-disc pl-5 mb-6 text-indigo-300 space-y-3"></ul>
    </div>
    <details class="conf-details mt-4">
  <summary
    class="primary-btn text-white font-medium py-2 px-4 rounded-lg inline-flex items-center justify-center w-full md:w-auto cursor-pointer"
  >
    <i class="fas fa-image mr-2"></i>
    <span class="conf-toggle-text">Show screenshot</span>

    <i class="fas fa-chevron-down ml-2 text-xs opacity-80 conf-chevron"></i>
  </summary>

  <div class="mt-3">
    <img
      src="/PuPrimeStep2.jpg"
      alt="Configuration example"
      class="w-full rounded-lg border border-slate-700/50"
      loading="lazy"
    />
  </div>
</details>
  </div>

  <!-- STEP 3 -->
  <div class="step-card rounded-xl p-4 md:p-8 mb-8">
    <div class="flex items-start mb-6">
      <div class="step-number rounded-full w-10 h-10 md:w-12 md:h-12 flex items-center justify-center text-white font-bold text-lg md:text-lg mr-3 md:mr-4 flex-shrink-0">3</div>
      <h2 id="step-1c-title" class="text-xl md:text-2xl font-bold text-white">
        Enter Your Details
      </h2>
    </div>
    <div class="ml-0 md:ml-16 pl-0 md:pl-2">
      <p id="step-1c-desc" class="mb-4">
        Fill in all required fields with your personal information:
      </p>
      <div class="grid grid-cols-1 gap-4 mb-6">
        <div class="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
          <h4 id="step-1c-after" class="font-bold text-white mb-2">
            Once your account has been confirmed.

          </h4>
          <ul id="step-1c-after-list" class="list-disc pl-5 space-y-1 text-sm"></ul>
        </div>
      </div>
          <details class="conf-details mt-4">
  <summary
    class="primary-btn text-white font-medium py-2 px-4 rounded-lg inline-flex items-center justify-center w-full md:w-auto cursor-pointer"
  >
    <i class="fas fa-image mr-2"></i>
    <span class="conf-toggle-text">Show screenshot</span>

    <i class="fas fa-chevron-down ml-2 text-xs opacity-80 conf-chevron"></i>
  </summary>

  <div class="mt-3">
    <img
      src="/PuPrimeStep3.jpg"
      alt="Configuration example"
      class="w-full rounded-lg border border-slate-700/50"
      loading="lazy"
    />
  </div>
</details>
    </div>
  </div>

 <!-- STEP 4 -->
<div class="step-card rounded-xl p-4 md:p-8 mb-8">
  <div class="flex items-start mb-6">
    <div
      class="step-number rounded-full w-10 h-10 md:w-12 md:h-12 flex items-center justify-center text-white font-bold text-lg md:text-lg mr-3 md:mr-4 flex-shrink-0"
    >
      4
    </div>
    <h2 id="step-1d-title" class="text-xl md:text-2xl font-bold text-white">
      Complete Your Registration
    </h2>
  </div>

  <div class="ml-0 md:ml-16 pl-0 md:pl-2">
    <p id="step-1d-desc" class="mb-4">
      After registering, you’ll receive an email from the broker. Enter the code you received or click the link to
      confirm your email address:
    </p>

    <div class="grid grid-cols-1 gap-4 mb-6">
      <div class="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
        <h4 id="step-1d-after" class="font-bold text-white mb-2">
          Once your account has been confirmed.
        </h4>

        <ul id="step-1d-after-list" class="list-disc pl-5 space-y-1 text-sm"></ul>

        <details class="conf-details mt-4">
          <summary
            class="primary-btn text-white font-medium py-2 px-4 rounded-lg inline-flex items-center justify-center w-full md:w-auto cursor-pointer"
          >
            <i class="fas fa-image mr-2"></i>
            <span class="conf-toggle-text">Show screenshot</span>
            <i class="fas fa-chevron-down ml-2 text-xs opacity-80 conf-chevron"></i>
          </summary>

          <div class="mt-3">
            <img
              src="/PuPrimeStep4.jpg"
              alt="Client Area screenshot"
              class="w-full rounded-lg border border-slate-700/50"
              loading="lazy"
            />
          </div>
        </details>
      </div>
    </div>

    <div class="info-bubble p-4 rounded-lg mb-4">
      <p id="step-1d-info" class="text-pink-200">
        <i class="fas fa-info-circle mr-2"></i>
       Identity verification is a mandatory process required by law for legal purposes.
      </p>
    </div>
  </div>
</div>

<!-- STEP 5 -->
<div class="step-card rounded-xl p-4 md:p-8 mb-8">
  <div class="flex items-start mb-6">
    <div
      class="step-number rounded-full w-10 h-10 md:w-12 md:h-12 flex items-center justify-center text-white font-bold text-lg md:text-lg mr-3 md:mr-4 flex-shrink-0"
    >
      5
    </div>
    <h2 id="step-1e-title" class="text-xl md:text-2xl font-bold text-white">
      Deposit Funds
    </h2>
  </div>

  <div class="ml-0 md:ml-16 pl-0 md:pl-2">
    <p id="step-1e-desc" class="mb-4">
      Once documents are confirmed via mail:
    </p>

    <!-- MATCH STEP 4 INNER BOX SIZE -->
    <div class="grid grid-cols-1 gap-4 mb-6">
      <div class="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
        <ol id="step-1e-list" class="list-decimal pl-5 space-y-3"></ol>
      </div>
    </div>

    <div class="tip-bubble p-4 rounded-lg">
      <p id="step-1e-tip" class="text-blue-200">
        <i class="fas fa-check-circle mr-2"></i>
        <strong>Important!</strong> You will be the sole owner of your account credentials and funds, which you can
        withdraw at any time.
      </p>
    </div>
  </div>
</div>


  <!-- FINAL -->
  <div class="text-center mt-16 mb-12">
    <h2 id="registration-complete-title" class="text-3xl font-bold text-white mb-4">
      Registration Complete!
    </h2>
    <p id="registration-complete-desc" class="text-xl text-indigo-300 mb-8 max-w-2xl mx-auto">
      You're now ready to configure Candy Bot Pro
    </p>
    <a id="proceed-config" href="#" class="primary-btn text-white font-medium py-3 px-8 rounded-lg inline-flex items-center">
      <i class="fas fa-cog mr-2"></i>
      Proceed to Bot Configuration
    </a>
  </div>

  <footer class="mt-16 pt-8 border-t border-slate-700/50 text-center">
    <p class="text-sm text-indigo-500">© 2025 Sweet Trading. All rights reserved.</p>
  </footer>
</div>
<!-- Image viewer (Guida3) -->
<div id="img-viewer" class="hidden fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm p-4 flex items-center justify-center">
  <div class="relative w-full max-w-4xl">
    <button
      type="button"
      data-close-img
      class="absolute -top-3 -right-3 bg-slate-900/90 border border-slate-700/60 text-white rounded-full w-10 h-10 flex items-center justify-center"
      aria-label="Close"
    >
      <i class="fas fa-times"></i>
    </button>

    <img
      id="img-viewer-img"
      src=""
      alt="Screenshot"
      class="w-full max-h-[85vh] object-contain rounded-lg border border-slate-700/60 bg-slate-900/30"
      loading="lazy"
    />
  </div>
</div>

<script>
  const translations = {
    en: {
      'conf-toggle-show': 'Show screenshot',
      'conf-toggle-hide': 'Hide screenshot',

      'header-title': 'Candy Bot Standard Guide',
      'header-subtitle': "Step-by-step setup to maximize your bot's performance",
      'get-started': 'Get Started <i class="fas fa-arrow-down ml-2"></i>',
      'step-1a-title': 'Create Your Trading Account 📊',
      'step-1a-desc': "Register on the broker's website using the button below:",
      'go-to-broker': 'Go to Broker',
      'pro-tip': '<i class="fas fa-lightbulb mr-2"></i><strong>Pro Tip:</strong> Keep two browser tabs open - one for this guide and one for registration.',

      'step-1b-title': 'Account creation',
      'step-1b-desc': 'On the page that appears register by entering:',
      'step-1b-list': [
        'First and last name',
        'Country',
        'Phone Number',
        'Email',
        'Password',
        'Then click "Continue"',
        'You will receive an email to confirm your email address.'
      ],

      'step-1c-title': 'Create the trading account',
      'step-1c-desc': '',
      'step-1c-after': 'Once in your personal area:',
      'step-1c-after-list': [
        'In the left-hand menu, click on "Accounts"',
        'then "Open Live Account"',
        'Select:',
        'Type: STANDARD',
        'Leverage: 1:300',
        'Currency: USD',
        'at the end "Continue"'
      ],

      'step-1d-title': 'Complete Your Registration',
      'step-1d-desc': '',
      'step-1d-after': 'In your account:',
      'step-1d-after-list': [
        'Inside your profile, click "Profile" in the left-hand menu.',
        'Then select "Upload Documents"',
        'Select the desired document, attach the photos, and click "Upload"'
      ],
      'step-1d-info': 'Identity verification is a mandatory process required by law for legal purposes.',

      'step-1d-notes': 'Once you are logged in:',
      'step-1d-notes-list': [
        'Click on “Personal Information section” in the yellow box that appears.',
        'Fill out the form with all the required information and save.',
        'Now click on the three lines in the top right corner.',
        'Select "Verification" and then "Verify account"',
        'Scroll down and click the “Identity Verification” icon.',
        'Now complete the identity verification (it will only take a few minutes).',
        'At the end complete the residence verification as well (the option right below that).'
      ],

      'step-1d-extra': 'Final step:',
      'step-1d-extra-list': [
        'Now click on the three lines in the top right corner.',
        'Select "Verification" and then "Verify Card"',
        'Scroll down, fill all the information as required and submit'
      ],

      'step-1d-infoo': 'Pay attention: document authentication is a very important process for protection and security, and it is legally mandatory—especially card verification, to ensure it hasn’t been stolen or involved in fraud. Please note: as specified by the broker, it is necessary to cover the card’s sensitive data (such as the CVC), show only the first 6 and the last 4 digits of the card number, and include the cardholder’s signature on the back.',

      'step-1e-title': 'Deposit Funds',
      'step-1e-desc': 'On the page that appears',
      'step-1e-list': [
        'Click the X in the top-right corner.',
        'Click on "Promotions" in the bottom-right corner.',
        'Click on "100% Deposit Bonus"',
        'Click on "Opt-in Now" (with this promo, the broker gives you a 100% deposit bonus).',
        'Then click on "Deposit Now"',
        'Select the amount (Min 100$)',
        'Click "Continue"',
        'Select the payment method and click "Continue"',
        'Once the payment is completed and confirmed by email, you can proceed with the bot setup.'
      ],

      'step-1e-tip': '<i class="fas fa-check-circle mr-2"></i><strong>Important!</strong> You will be the sole owner of your account credentials and funds, which you can withdraw at any time.',
      'registration-complete-title': 'Registration Complete!',
      'registration-complete-desc': "You're now ready to configure Candy Bot Pro",
      'proceed-config': '<i class="fas fa-cog mr-2"></i> Proceed to Bot Configuration'
    },

    it: {
      'conf-toggle-show': 'Mostra screenshot',
      'conf-toggle-hide': 'Nascondi screenshot',

      'header-title': 'Guida Candy Bot Standard',
      'header-subtitle': 'Configurazione passo passo per massimizzare le prestazioni del tuo bot',
      'get-started': 'Inizia ora <i class="fas fa-arrow-down ml-2"></i>',
      'step-1a-title': 'Crea il tuo account di trading 📊',
      'step-1a-desc': 'Registrati sul sito del broker usando il pulsante qui sotto:',
      'go-to-broker': 'Vai al broker',
      'pro-tip': '<i class="fas fa-lightbulb mr-2"></i><strong>Consiglio Pro:</strong> tieni aperte due schede del browser – una per questa guida e una per la registrazione.',

      'step-1b-title': 'Creazione dell’account',
      'step-1b-desc': 'Nella pagina che appare registrati inserendo:',
      'step-1b-list': [
        'Nome e cognome',
        'Paese',
        'Numero di telefono',
        'Email',
        'Password',
        'Infine clicca su "Continua".',
        'Riceverai dopo una email con cui confermare il tuo indirizzo email.'
      ],

      'step-1c-title': "Crea l'account di trading",
      'step-1c-desc': "Una volta all'interno del tuo profilo:",
      'step-1c-after': 'Nel menù che appare a sinistra:',
      'step-1c-after-list': [
        'Clicca su "Accounts"',
        'Seleziona "Open Live Account"',
        'Nel form che appare seleziona:',
        'Type: STANDARD',
        'Leverage: 1:300',
        'Currency: USD',
        'Infine clicca su "Continue"'
      ],

      'step-1d-title': 'Completa la registrazione',
      'step-1d-desc': 'Sempre nel menù di sinistra',
      'step-1d-after': 'Seleziona:',
      'step-1d-after-list': [
        'Seleziona "Profile"',
        'Seleziona "Upload Documents"',
        'Seleziona "ID/Passport"',
        'Compila tutti i campi richiesti e carica fronte e retro del documento',
        'Infine clicca su "Upload"'
      ],
     'step-1d-info': "La verifica dell'identità è un processo obbligatorio richiesto dalla normativa vigente per finalità legali.",
      'step-1d-notes': 'Una volta loggato:',
      'step-1d-notes-list': [
        'Clicca su "Personal Information section" nel riquadro giallo che appare',
        'Compila il form con tutte le informazioni richieste e salva.',
        'Ora clicca sulle 3 linee in alto a destra',
        'Seleziona la voce "Verification" e poi "Verify Account"',
        'Ora scorri fino a trovare l’icona "Identity verification"',
        'Infine clicca su di essa e completa la verifica dell’identità (richiederà pochi minuti)',
        'Per terminare, completa anche la verifica della residenza (la voce appena sotto "Verification of residence")'
      ],

     'step-1d-extra': 'Step finale',
     'step-1d-extra-list': [
       'Clicca sulle 3 linee in alto a destra',
       'Seleziona la voce "Verification" e poi "Verify Card"',
       'Infine scorri verso il basso fino a trovare il form da compilare',
       'Inserisci tutti i dati richiesti e clicca "Upload"'
     ],
     
     'step-1d-infoo': 'Presta attenzione: la verifica/autenticazione dei documenti è una procedura molto importante per la tutela e la sicurezza ed è obbligatoria per legge, in particolare quella della carta per verificare che non sia stata rubata o sia stata oggetto di truffe. Mi raccomando: come specificato dal broker, è necessario coprire i dati sensibili della carta (come il CVC), mostrare solo le prime 6 e le ultime 4 cifre del numero della carta e la firma del titolare sul retro.',

'step-1e-title': 'Deposita fondi',
      'step-1e-desc': 'Nella pagina che appare',
      'step-1e-list': [
        'Clicca la X in alto a destra.',
        'Clicca su "Promotions" in basso a destra.',
        'Clicca su "100% Deposit Bonus".',
        'Clicca su "Opt-in Now" (con questa promo, il broker ti dà un bonus del 100% sul deposito).',
        'Poi clicca su "Deposit Now".',
        "Seleziona l'importo (min 100$).",
        'Clicca su "Continue".',
        'Seleziona il metodo di pagamento e clicca su "Continue".',
        'Una volta completato e confermato il pagamento via email, puoi procedere con la configurazione del bot.'
      ],

      'step-1e-tip': '<i class="fas fa-check-circle mr-2"></i><strong>Importante!</strong> Sarai l’unico proprietario delle credenziali del tuo account e dei fondi, che potrai prelevare in qualsiasi momento.',
      'registration-complete-title': 'Registrazione completata!',
      'registration-complete-desc': 'Ora sei pronto per configurare Candy Bot Pro',
      'proceed-config': '<i class="fas fa-cog mr-2"></i> Procedi alla configurazione del bot'
    }
  };


  function setListContent(id, items) {
    var el = document.getElementById(id);
    if (!el) return;
    el.innerHTML = '';
    items.forEach(function (text) {
      var li = document.createElement('li');
      li.innerHTML = text;
      el.appendChild(li);
    });
  }

  // elementi che DEVONO restare html (icona dentro)
  var htmlKeys = new Set(['pro-tip', 'step-1e-tip', 'get-started', 'proceed-config']);

  function translatePage(lang) {
    var t = translations[lang] || translations.en;

    Object.keys(t).forEach(function (key) {
      var value = t[key];
      var el = document.getElementById(key);
      if (!el) return;

      if (Array.isArray(value)) {
        setListContent(key, value);
      } else if (htmlKeys.has(key)) {
        el.innerHTML = value;
      } else {
        el.textContent = value;
      }
    });
  }

  function getLang() {
    var sel = document.getElementById('language-selector');
    return (sel && sel.value) ? sel.value : 'en';
  }

  // ✅ aggiorna SOLO le label dei <details class="conf-details">
  function updateConfDetailsLabels(lang) {
    var t = translations[lang] || translations.en;

    document.querySelectorAll('details.conf-details').forEach(function (d) {
      var span = d.querySelector('.conf-toggle-text');
      if (!span) return;

      span.textContent = d.open
        ? (t['conf-toggle-hide'] || 'Hide screenshot')
        : (t['conf-toggle-show'] || 'Show screenshot');
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    var selector = document.getElementById('language-selector');
    var initial = (selector && selector.value) ? selector.value : 'en';

    translatePage(initial);
    updateConfDetailsLabels(initial);

    // aggiorna label quando apri/chiudi ogni <details>
    document.querySelectorAll('details.conf-details').forEach(function (d) {
      d.addEventListener('toggle', function () {
        updateConfDetailsLabels(getLang());
      });
    });

    // cambio lingua
    if (selector) {
      selector.addEventListener('change', function () {
        translatePage(this.value);
        updateConfDetailsLabels(this.value);
      });
    }

    // smooth scroll "Get Started"
    var gs = document.getElementById('get-started');
    if (gs) {
      gs.addEventListener('click', function (e) {
        e.preventDefault();
        var target = document.getElementById('step-1');
        if (target && target.scrollIntoView) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }

    // apri guida setup (postMessage al parent)
    var pc = document.getElementById('proceed-config');
    if (pc) {
      pc.addEventListener('click', function (e) {
        e.preventDefault();
        try {
          if (window.parent && window.parent !== window) {
            window.parent.postMessage({ type: 'OPEN_SETUP_GUIDE' }, '*');
          }
        } catch (_) {}
      });
    }
  });
</script>

<script>
  function copyText(text, btn) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }

    // feedback "Copied!"
    const original = btn.innerHTML;
    btn.innerHTML = '<span>Copied!</span><i class="fas fa-check"></i>';
    btn.disabled = true;
    setTimeout(() => {
      btn.innerHTML = original;
      btn.disabled = false;
    }, 1200);
  }
</script>

</body>
</html>`;


/* === HTML guida SETUP per iframe (seconda parte) === */
const GUIDE_HTML_SETUP = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Candy Bot Pro Setup - Sweet Trading</title>
  <script src="https://cdn.tailwindcss.com"></script>
  <link
    rel="stylesheet"
    href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css"
  />
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
    :root { --primary: #6366f1; --secondary: #8b5cf6; --accent: #ec4899; }
    body {
      font-family: 'Inter', sans-serif;
      background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #1e1b4b 100%);
      color: #e2e8f0;
    }
    .header-gradient {
      background: linear-gradient(
        135deg,
        rgba(15, 23, 42, 0.95) 0%,
        rgba(30, 41, 59, 0.95) 100%
      );
      backdrop-filter: blur(10px);
    }
    .step-number {
      background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
      box-shadow: 0 4px 15px rgba(99, 102, 241, 0.3);
    }
    .step-card {
      background: rgba(15, 23, 42, 0.7);
      backdrop-filter: blur(8px);
      border: 1px solid rgba(99, 102, 241, 0.2);
      transition: all 0.3s ease;
    }
    .step-card:hover {
      border-color: rgba(99, 102, 241, 0.4);
      transform: translateY(-2px);
      box-shadow: 0 10px 25px -5px rgba(99, 102, 241, 0.1);
    }
    .primary-btn {
      background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
      transition: all 0.3s ease;
    }
    .primary-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px -5px rgba(99, 102, 241, 0.4);
    }
    .secondary-btn {
      background: rgba(99, 102, 241, 0.2);
      border: 1px solid rgba(99, 102, 241, 0.4);
      transition: all 0.3s ease;
    }
    .secondary-btn:hover { background: rgba(99, 102, 241, 0.3); transform: translateY(-2px); }
    .info-bubble { background: rgba(236, 72, 153, 0.1); border-left: 4px solid var(--accent); }
    .tip-bubble { background: rgba(59, 130, 246, 0.1); border-left: 4px solid #3b82f6; }
    .animate-float { animation: float 3s ease-in-out infinite; }
    @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
    .feature-card {
      background: rgba(15, 23, 42, 0.5);
      border: 1px solid rgba(99, 102, 241, 0.2);
      transition: all 0.3s ease;
    }
    .feature-card:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 25px -5px rgba(99, 102, 241, 0.2);
      border-color: rgba(99, 102, 241, 0.4);
    }
    .progress-bar {
      height: 6px;
      background: rgba(99, 102, 241, 0.2);
      border-radius: 3px;
      overflow: hidden;
    }
    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, var(--primary) 0%, var(--secondary) 100%);
      border-radius: 3px;
      width: 91%;
    }
  </style>
</head>
<body class="min-h-screen">

  <header
    class="header-gradient w-full flex justify-between items-center px-6 py-4 sticky top-0 z-50 border-b border-slate-700/50"
  >
    <div class="flex items-center gap-4">
      <div class="flex items-center">
        <img
          src="/logo1-st.png"
          alt="Sweet Trading Logo"
          class="h-12 w-auto animate-float"
        />
        <span
          class="ml-3 text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-300 to-purple-300 hidden md:block"
        ></span>
      </div>
    </div>

    <div class="flex items-center gap-4">
      <a id="back-link"
         href="#"
         class="secondary-btn text-white font-medium py-2 px-4 rounded-lg inline-flex items-center text-sm relative z-50">
        <i class="fas fa-arrow-left mr-2"></i>
        <span id="back-link-text">Back to previous guide</span>
      </a>

      <select
        id="language-selector"
        class="bg-slate-800/50 text-white border border-slate-600 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        <option value="en">🇬🇧 English</option>
        <option value="it">🇮🇹 Italiano</option>
      </select>
    </div>
  </header>

  <section class="relative py-20 px-6 overflow-hidden">
    <div class="absolute inset-0 opacity-20">
      <div
        class="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-indigo-900/50 to-transparent"
      ></div>
      <div
        class="absolute bottom-0 right-0 w-1/3 h-full bg-gradient-to-l from-purple-900/50 to-transparent"
      ></div>
    </div>

    <div class="relative max-w-4xl mx-auto text-center">
      <h1
        id="hero-title"
        class="text-4xl md:text-5xl font-bold mb-6 text-white"
      >
        <span
          class="bg-clip-text text-transparent bg-gradient-to-r from-indigo-300 to-pink-400"
        >
          Candy Bot Pro Setup
        </span>
      </h1>
      <p id="hero-subtitle" class="text-xl text-indigo-300 max-w-2xl mx-auto">
        Complete guide to configure and optimize your bot
      </p>

      <div class="mt-10 flex justify-center gap-4">
        <a
          href="#step-1"
          id="start-setup-btn"
          class="primary-btn text-white font-medium py-3 px-6 rounded-lg inline-flex items-center"
        >
          Start Setup <i class="fas fa-arrow-down ml-2"></i>
        </a>
      </div>
    </div>
  </section>

  <div class="container mx-auto px-4 py-12 max-w-4xl">
    <div class="step-card rounded-xl p-8 mb-12 text-center">
      <div class="flex justify-center mb-6">
        <div class="bg-indigo-900/20 p-4 rounded-full">
          <i class="fas fa-robot text-4xl text-indigo-300"></i>
        </div>
      </div>
      <h2 id="intro-title" class="text-2xl font-bold text-white mb-4">
        Ready to configure Candy Bot Pro?
      </h2>
      <p id="intro-desc" class="text-indigo-300 mb-6">
        Follow these final steps to optimize your bot and start automated trading
      </p>
      <div class="flex flex-wrap justify-center gap-3">
        <span
          id="time-estimate"
          class="bg-indigo-900/50 text-indigo-200 text-xs font-medium px-2.5 py-1 rounded-full"
        >
          Estimated time: 5 min
        </span>
        <span
          id="difficulty"
          class="bg-pink-900/50 text-pink-200 text-xs font-medium px-2.5 py-1 rounded-full"
        >
          Difficulty: Easy
        </span>
      </div>
    </div>

<div id="step-1" class="step-card rounded-xl p-4 md:p-8 mb-8">
  <div class="flex items-start mb-6">
    <div class="step-number rounded-full w-10 h-10 md:w-12 md:h-12 flex items-center justify-center text-white font-bold text-lg md:text-lg mr-3 md:mr-4 flex-shrink-0">1</div>
    <div>
      <h2 id="step-1a-title" class="text-xl md:text-2xl font-bold text-white mb-2">
        Install the trading platform 📈
      </h2>
      <div class="flex flex-wrap gap-2 mb-4">
        <span class="bg-indigo-900/50 text-indigo-200 text-xs font-medium px-2.5 py-0.5 rounded">Getting Started</span>
        <span class="bg-pink-900/50 text-pink-200 text-xs font-medium px-2.5 py-0.5 rounded">Required</span>
      </div>
    </div>
  </div>

  <div class="ml-0 md:ml-16 pl-0 md:pl-2">
    <div class="prose prose-invert max-w-none">
      <p id="step-1a-desc" class="mb-4">
        Login in to your account using the Official Broker app:
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <a
          href="${APP_CONFIG.URLs.Ios}"
          target="_blank"
          rel="noopener noreferrer"
          id="go-to-broker-ios"
          class="primary-btn text-white font-medium py-3 px-6 rounded-lg text-center"
        >
          iOS
        </a>

        <a
          href="${APP_CONFIG.URLs.Android}"
          target="_blank"
          rel="noopener noreferrer"
          id="go-to-broker-android"
          class="primary-btn text-white font-medium py-3 px-6 rounded-lg text-center"
        >
          Android
        </a>
      </div>
      

     
    </div>
  </div>
</div>
    

   <div id="step-2" class="step-card rounded-xl p-8 mb-8">
      <div class="flex items-start">
        <div class="step-number rounded-full w-12 h-12 flex items-center justify-center text-white font-bold text-lg mr-4 flex-shrink-0">2</div>
        <div class="w-full">
          <h2 id="step-2-title" class="text-2xl font-bold text-white mb-3">
            Bot Connection
          </h2>

          <div class="mb-4">
            <span id="time-estimate-tag" class="bg-indigo-900/50 text-indigo-200 text-xs font-medium px-2.5 py-1 rounded-full">
              Estimated time: 3 min
            </span>
          </div>


          
          <ul id="step-2-list" class="list-disc pl-5 mb-6 text-indigo-300 space-y-3">
            <li>From your personal area, click on “Copytrading” from the menu on the left.</li>
            <li>Agree to terms and conditions and choose "CopyTrade Investor".</li>
            <li>At the top, select the blue “Copytrading” box.</li>
            <li>In the search bar, type “Candy-Bot Standard”.</li>
           <li>
  Make sure that “Choose instruments to copy” is set to all ForexMart trading instruments and “Daily copying” is set to All.
  Then only edit the “Copying ratio” field – it must be set to 1.

  <button
    type="button"
    class="ml-2 inline-flex items-center gap-2 bg-indigo-900/40 hover:bg-indigo-900/60 text-indigo-100 text-xs font-semibold px-3 py-1 rounded-full align-middle transition"
    onclick="copyText('CandyBotStandard', this)"
    aria-label="Copy CandyBotStandard"
    title="Copy"
  >
    <span>CandyBotStandard</span>
    <i class="fas fa-copy"></i>
  </button>
</li>
            <li>After checking that all the fields are correct, click “Subscribe” and then “Confirm”.</li>
            <li>If you’ve connected correctly, a confirmation message will appear and you’ll also receive a confirmation email.</li>
          </ul>
        </div>
      </div>

      <div class="mt-6 pt-6 border-t border-indigo-800/50">
        <p id="congrats-desc" class="text-lg font-semibold text-green-400 text-center">
          You're officially connected to Candy Bot! 🎉
        </p>
      </div>
    </div>

    <div class="step-card rounded-xl p-8 mb-8 border border-green-500/30">
      <div class="flex items-start mb-6">
        <div class="bg-green-900/20 p-3 rounded-full mr-4">
          <i class="fas fa-gift text-green-300 text-xl"></i>
        </div>
        <div>
          <h2 id="bonus-title" class="text-2xl font-bold text-white mb-2">
            Bonus Tips
          </h2>
          <p id="bonus-desc" class="text-indigo-300">
            Optimize your experience with these additional suggestions
          </p>
        </div>
      </div>

    <div class="mt-2 flex justify-center">
  <div class="w-full max-w-3xl">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 place-items-center">
      
      <!-- Monitoring -->
      <div class="feature-card p-5 rounded-lg w-full">
        <div class="flex items-center mb-3 justify-center md:justify-start">
          <div class="bg-indigo-900/20 p-2 rounded-full mr-3">
            <i class="fas fa-chart-line text-indigo-300"></i>
          </div>
          <h4 id="bonus-monitoring" class="font-bold text-white">Monitoring</h4>
        </div>
        <p id="bonus-monitoring-desc" class="text-sm text-indigo-300 text-center md:text-left">
          Regularly check bot performance through your ForexMart account.
        </p>
      </div>

      <!-- Telegram -->
      <div class="feature-card p-5 rounded-lg w-full">
        <div class="flex items-center mb-3 justify-center md:justify-start">
          <div class="bg-sky-900/20 p-2 rounded-full mr-3">
            <i class="fa-brands fa-telegram text-sky-300"></i>
          </div>
          <h4 id="bonus-telegram" class="font-bold text-white">Telegram</h4>
        </div>
        <p id="bonus-telegram-desc" class="text-sm text-indigo-300 text-center md:text-left">
          For any difficulties, please contact our support team via email or Telegram.
        </p>
      </div>

    </div>
  </div>
</div>

    <div class="text-center mt-16 mb-12">
      <h2 id="final-title" class="text-3xl font-bold text-white mb-4">
        Setup Complete!
      </h2>
      <p id="final-desc" class="text-xl text-indigo-300 mb-8 max-w-2xl mx-auto">
        Now relax while Candy Bot brings you sweet profits.
      </p>
    </div>

    <footer class="mt-16 pt-8 border-t border-slate-700/50 text-center">
      <p id="footer-text" class="text-sm text-indigo-500">
        © 2025 Sweet Trading. All rights reserved.
      </p>
    </footer>
  </div>
<!-- Image viewer (Guida3) -->
<div id="img-viewer" class="hidden fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm p-4 flex items-center justify-center">
  <div class="relative w-full max-w-4xl">
    <button
      type="button"
      data-close-img
      class="absolute -top-3 -right-3 bg-slate-900/90 border border-slate-700/60 text-white rounded-full w-10 h-10 flex items-center justify-center"
      aria-label="Close"
    >
      <i class="fas fa-times"></i>
    </button>

    <img
      id="img-viewer-img"
      src=""
      alt="Screenshot"
      class="w-full max-h-[85vh] object-contain rounded-lg border border-slate-700/60 bg-slate-900/30"
      loading="lazy"
    />
  </div>
</div>

  <script>
    var translationsSetup = {
      en: {
      refresh: 'Refresh',
      
        'back-link-text': 'Back to previous guide',
        'hero-title': 'Candy Bot Pro Setup',
        'hero-subtitle': 'Complete guide to configure and optimize your bot',
        'start-setup-btn': 'Start Setup',
        'intro-title': 'Ready to configure Candy Bot Pro?',
        'intro-desc': 'Follow these final steps to optimize your bot and start automated trading',
        'time-estimate': 'Estimated time: 5 min',
        'difficulty': 'Difficulty: Easy',
        'step-2-title': 'Bot Connection',
        'time-estimate-tag': 'Estimated time: 3 min',

        'go-to-broker-ios': 'iOS',
       'go-to-broker-android': 'Android',
       'step-1a-title': 'Install the trading platform 📈',

       'step-1a-desc': "Install and log in to the broker’s official app by clicking below:",




        
       
               'step-2-list': [
          'Once installed and logged in:',
          'Click on "Discover" at the bottom center.',
          'Tap the search bar and enter the following name: ',
'<button type="button" ' +
  'class="inline-flex items-center gap-2 bg-indigo-900/40 hover:bg-indigo-900/60 text-indigo-100 text-xs font-semibold px-3 py-1 rounded-full align-middle transition" ' +
  'onclick="copyText(\\'CandyBotStandard\\', this)" ' +
  'aria-label="Copy CandyBotStandard" title="Copy">' +
    '<span>CandyBotStandard</span>' +
    '<i class="fas fa-copy"></i>' +
'</button>',
          'Click on "Copy".',
          'Make sure the following settings are exactly the same:',
          'Fixed Lots.',
          'Investment: Min 100',
          'Lots per Order: 0.01',
          'Stop Loss: 95%',
          'Once you’ve confirmed the settings are correct click on "Submit".'
        ],

        'congrats-desc': "You're officially connected to Candy Bot! 🎉",
        'bonus-title': 'Bonus Tips',
        'bonus-desc': 'Optimize your experience with these additional suggestions',
        'bonus-monitoring': 'Monitoring',
        'bonus-monitoring-desc': 'Regularly check bot performance through your ForexMart account.',
        'bonus-notifications': 'Education',
        'bonus-notifications-desc': 'Join our academy to learn more about trading and how the bot works.',
        'bonus-telegram': 'Telegram',
        'bonus-telegram-desc': 'For any difficulties, please contact our support team via email or Telegram.',
        'bonus-support': 'Community',
        'bonus-support-desc': 'Join our Telegram community to stay updated and share your experience with other users.',
        'final-title': 'Setup Complete!',
        'final-desc': 'Now relax while Candy Bot brings you sweet profits.',
        'footer-text': '© 2025 Sweet Trading. All rights reserved.'
      },
      it: {
      refresh: 'Aggiorna',
        'back-link-text': 'Torna alla guida precedente',
        'hero-title': 'Configurazione Candy Bot Pro',
        'hero-subtitle': 'Guida completa per configurare e ottimizzare il tuo bot',
        'start-setup-btn': 'Inizia la configurazione',
        'intro-title': 'Pronto a configurare Candy Bot Pro?',
        'intro-desc': 'Segui questi passaggi finali per ottimizzare il tuo bot e iniziare il trading automatico',
        'time-estimate': 'Tempo stimato: 5 min',
        'difficulty': 'Difficoltà: Facile',
        'step-2-title': 'Connessione al Bot',
        'time-estimate-tag': 'Tempo stimato: 3 min',

        'go-to-broker-ios': 'iOS',
       'go-to-broker-android': 'Android',
       'step-1a-title': 'Installa la piattaforma di trading 📈',

       'step-1a-desc': 'Installa e accedi all’app ufficiale del broker cliccando qui sotto:',



        
        'step-2-desc': 'In questo passaggio ti connetterai al bot. Non serve computer o VPS, ti basta il telefono 📲',
             'step-2-list': [
        'Una volta installata l’app ed effettuato l’accesso:',
       'Clicca sulla barra di ricerca ed inserisci il seguente nome: ',
'<button type="button" ' +
  'class="inline-flex items-center gap-2 bg-indigo-900/40 hover:bg-indigo-900/60 text-indigo-100 text-xs font-semibold px-3 py-1 rounded-full align-middle transition" ' +
  'onclick="copyText(\\'CandyBotStandard\\', this)" ' +
  'aria-label="Copia CandyBotStandard" title="Copia">' +
    '<span>CandyBotStandard</span>' +
    '<i class="fas fa-copy"></i>' +
'</button>',
        'Clicca su "Copy".',
        'Assicurati che i seguenti settaggi siano esattamente gli stessi:',
        'Lotti fissi.',
        'Investimento: Min 100',
        'Lotti per ordine: 0.01',
        'Stop Loss: 95%',
        'Una volta verificata la correttezza dei settaggi, clicca su "Submit".'
      ],
        'congrats-desc': 'Sei ufficialmente connesso a Candy Bot! 🎉',
        'bonus-title': 'Consigli Bonus',
        'bonus-desc': 'Ottimizza la tua esperienza con questi suggerimenti aggiuntivi',
        'bonus-monitoring': 'Monitoraggio',
        'bonus-monitoring-desc': 'Controlla regolarmente le prestazioni del bot dal tuo account ForexMart.',
        'bonus-notifications': 'Formazione',
        'bonus-notifications-desc': 'Unisciti alla nostra academy per capire meglio il trading e il funzionamento del bot.',
        'bonus-telegram': 'Telegram',
        'bonus-telegram-desc': 'Per qualsiasi difficoltà, contatta il nostro supporto via email o Telegram.',
        'bonus-support': 'Community',
        'bonus-support-desc': 'Unisciti alla nostra community Telegram per restare aggiornato e condividere la tua esperienza con altri utenti.',
        'final-title': 'Configurazione completata!',
        'final-desc': 'Ora rilassati mentre Candy Bot lavora per portarti profitti costanti.',
        'footer-text': '© 2025 Sweet Trading. Tutti i diritti riservati.'
      }
    };

    function setListContent(id, items) {
      var el = document.getElementById(id);
      if (!el) return;
      el.innerHTML = '';
      items.forEach(function(text) {
        var li = document.createElement('li');
        li.innerHTML = text;
        el.appendChild(li);
      });
    }

    function translateSetup(lang){
      var t = translationsSetup[lang] || translationsSetup.en;
      Object.keys(t).forEach(function(key){
        var value = t[key];
        var el = document.getElementById(key);
        if(!el) return;

        if (Array.isArray(value)) {
          setListContent(key, value);
        } else if (key === 'start-setup-btn' && el.tagName === 'A') {
          var icon = el.querySelector('i');
          var iconHTML = icon ? icon.outerHTML : '';
          el.innerHTML = value + ' ' + iconHTML;
        } else {
          el.textContent = value;
        }
      });
    }

    // ✅ QUI: SENZA aprire un altro <script>
      document.addEventListener('DOMContentLoaded', function(){
      var selector = document.getElementById('language-selector');
      var initial = (selector && selector.value) ? selector.value : 'en';

      translateSetup(initial);

      if(selector){
        selector.addEventListener('change', function(){
          translateSetup(this.value);
        });
      }

      // smooth scroll start
      var startBtn = document.getElementById('start-setup-btn');
      if (startBtn) {
        startBtn.addEventListener('click', function(e){
          e.preventDefault();
          var target = document.getElementById('step-1');
          if (target && target.scrollIntoView) target.scrollIntoView({ behavior:'smooth', block:'start' });
        });
      }

      // back to standard guide
      var back = document.getElementById('back-link');
      if (back) {
        back.addEventListener('click', function(e){
          e.preventDefault();
          try {
            if (window.parent && window.parent !== window) {
              window.parent.postMessage({ type:'BACK_TO_STANDARD_GUIDE' }, '*');
            }
          } catch(_) {}
        });
      }
    });
  </script>

  <script>
  function copyText(text, btn) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text);
    } else {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }

    if (btn) {
      var original = btn.innerHTML;
      btn.innerHTML = '<span>Copied!</span><i class="fas fa-check"></i>';
      btn.disabled = true;
      setTimeout(function () {
        btn.innerHTML = original;
        btn.disabled = false;
      }, 1200);
    }
  }
</script>

</body>
</html>`;


/* Global overrides */
const GlobalStyles = () => (
  <style id="contrast-fix">{`
    :root, body, #root { color: rgba(234,240,247,0.96) !important; background: ${APP_CONFIG.BRAND.layoutBg} !important; }
    .ant-typography, .ant-card, .ant-drawer, .ant-collapse, .ant-modal,
    .ant-badge, .ant-alert, .ant-menu, .ant-tag, .ant-form, .ant-descriptions,
    .ant-statistic, .ant-segmented, .ant-message-notice-content,
    .ant-popover-inner, .ant-tooltip-inner, .ant-list, .ant-table, .ant-steps, .ant-empty-description {
      color: rgba(234,240,247,0.96) !important;
    }
    .ant-btn:not(.ant-btn-primary):not(.ant-btn-link):not(.ant-btn-text) {
      color: rgba(234,240,247,0.96) !important; border-color: rgba(234,240,247,0.24) !important; background: transparent !important;
    }
    .ant-btn > span, .ant-btn .anticon { color: inherit !important; }
    .ant-menu .ant-menu-item, .ant-menu .ant-menu-submenu-title { color: rgba(234,240,247,0.9) !important; }
    .ant-menu .ant-menu-item-selected, .ant-menu .ant-menu-item:hover, .ant-menu .ant-menu-submenu-title:hover { color: #fff !important; }
    .ant-input, .ant-input-affix-wrapper input, textarea, .ant-select-selector, .ant-picker {
      color: rgba(234,240,247,0.96) !important; background: rgba(255,255,255,0.06) !important; border-color: rgba(255,255,255,0.18) !important;
    }
    .ant-input::placeholder, .ant-input-affix-wrapper input::placeholder, textarea::placeholder {
      color: rgba(234,240,247,0.55) !important; opacity: 1 !important;
    }
    .profile-card-light, .profile-card-light .ant-typography, .profile-card-light .ant-badge-status-text,
    .profile-card-light .ant-typography span, .profile-card-light .ant-typography p { color: #111 !important; }
  `}</style>
);

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState('en');
  const [collapsed, setCollapsed] = useState(false);
  const [activeKey, setActiveKey] = useState('dashboard');
  const [saveLoading, setSaveLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideType, setGuideType] = useState('standard'); // 'standard' | 'setup'
  const [pwdLoading, setPwdLoading] = useState(false);

  const [refreshTick, setRefreshTick] = useState(0);

  // Change password states
  const [pwdOld, setPwdOld] = useState('');
  const [pwdNew, setPwdNew] = useState('');
  const [pwdNew2, setPwdNew2] = useState('');

  // Delete account states
  const [delOpen, setDelOpen] = useState(false);
  const [delConfirm, setDelConfirm] = useState('');
  const [delPwd, setDelPwd] = useState('');
  const [delLoading, setDelLoading] = useState(false);

  // Academy UI states (integrata)
  const [academyLegendOpen, setAcademyLegendOpen] = useState(false);
  const [academyLevel, setAcademyLevel] = useState('beginner'); // beginner | intermediate
  const [academyBeginnerIndex, setAcademyBeginnerIndex] = useState(0);
  const [academyIntermediateIndex, setAcademyIntermediateIndex] = useState(0);

  const screens = useBreakpoint();
  const { token } = useToken();

  const referralLink = useMemo(() => `${APP_CONFIG.URLs.baseReferral}${user?.uid || ""}`, [user?.uid]);
  const isMobile = useMemo(() => !screens.md, [screens.md]);
  const isSmallMobile = useMemo(() => !screens.sm, [screens.sm]);

  const blackMsg = (content, type='success') =>
    message.open({ type, content: <span style={{ color: '#000' }}>{content}</span> });

  const copyToClipboard = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      blackMsg(lang === 'it' ? 'Copiato negli appunti' : 'Copied', 'success');
    } catch {
      blackMsg(lang === 'it' ? 'Errore nella copia' : 'Copy failed', 'error');
    }
  }, [lang]);

 const handleRefresh = useCallback(async () => {
  try {
    setLoading(true);

    // (opzionale) piccola attesa “smooth”
    await new Promise(r => setTimeout(r, 350));

    // ✅ forza remount della sezione corrente (soft reload)
    setRefreshTick(t => t + 1);

    blackMsg(lang === 'it' ? 'Dati aggiornati' : 'Updated', 'success');
  } catch {
    blackMsg(lang === 'it' ? 'Errore' : 'Error', 'error');
  } finally {
    setLoading(false);
  }
}, [lang]);


  const handleLogout = useCallback(() => {
    Modal.confirm({
      title: lang === 'it' ? 'Conferma logout' : 'Confirm logout',
      content: lang === 'it' ? 'Sei sicuro di voler uscire?' : 'Are you sure you want to logout?',
      okText: lang === 'it' ? 'Esci' : 'Logout',
      cancelText: lang === 'it' ? 'Annulla' : 'Cancel',
      onOk: logout,
    });
  }, [logout, lang]);

 const handleMenuClick = useCallback((key) => {
  if (key === 'logout') { handleLogout(); return; }

  if (key === 'refresh') {
    handleRefresh();
    if (isMobile) setMobileMenuOpen(false);
    return;
  }

  setActiveKey(key);
  if (isMobile) setMobileMenuOpen(false);
}, [isMobile, handleLogout, handleRefresh]);


  const handleSaveSettings = useCallback(async () => {
    setSaveLoading(true); await new Promise(r => setTimeout(r, 1000));
    blackMsg(lang === 'it' ? 'Impostazioni salvate' : 'Settings saved', 'success');
    setSaveLoading(false);
  }, [lang]);

  // Change password
  const handleChangePassword = useCallback(async () => {
    if (!pwdOld || !pwdNew || !pwdNew2) {
      blackMsg(lang === 'it' ? 'Compila tutti i campi' : 'Fill all fields', 'error');
      return;
    }
    if (pwdNew !== pwdNew2) {
      blackMsg(lang === 'it' ? 'Le password non coincidono' : 'Passwords do not match', 'error');
      return;
    }
    if (pwdNew.length < 8) {
      blackMsg(lang === 'it' ? 'Password troppo corta (min 8)' : 'Password too short (min 8)', 'error');
      return;
    }
    setPwdLoading(true);
    try {
      const auth = getAuth();
      const u = auth.currentUser;
      if (!u || !u.email) throw new Error('no-user');

      const cred = EmailAuthProvider.credential(u.email, pwdOld);
      await reauthenticateWithCredential(u, cred);
      await fbUpdatePassword(u, pwdNew);

      blackMsg(lang === 'it' ? 'Password aggiornata' : 'Password updated', 'success');
      setPwdOld(''); setPwdNew(''); setPwdNew2('');
      unlockZoom();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const msg =
        err?.code === 'auth/wrong-password' ? (lang === 'it' ? 'Password attuale errata' : 'Wrong current password') :
        err?.code === 'auth/weak-password' ? (lang === 'it' ? 'Password debole' : 'Weak password') :
        (lang === 'it' ? 'Errore durante l\'aggiornamento' : 'Update failed');
      blackMsg(msg, 'error');
    } finally {
      setPwdLoading(false);
    }
  }, [pwdOld, pwdNew, pwdNew2, lang]);

  // listener messaggi dalle guide (iframe)
  useEffect(() => {
    const handler = (event) => {
      if (!event.data || typeof event.data !== 'object') return;
      if (event.data.type === 'OPEN_SETUP_GUIDE') {
        setGuideType('setup');
        setGuideOpen(true);
      }
      if (event.data.type === 'BACK_TO_STANDARD_GUIDE') {
        setGuideType('standard');
        setGuideOpen(true);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const cardVariants = { offscreen: { y: 10, opacity: 0 }, onscreen: { y: 0, opacity: 1, transition: { type: "spring", bounce: 0.2, duration: 0.5 } } };

  const t = useMemo(() => ({
    en: {
      refresh: 'Refresh',
      welcome:'Welcome back',
      earnWithFriends:'Earn With Friends', referralDesc:'Invite friends and earn up to 30% commission', shareLink:'Share',
      personalSettings:'Settings', name:'Name', email:'Email', save:'Save',
      address:'Address', city:'City', zip:'ZIP Code', country:'Country', state:'State', phone:'Phone',
      logout:'Logout', menu:'Menu',
      guide:'Candy Bot Standard Guide',
      account:'Account', changePassword:'Change password', currentPassword:'Current password',
      newPassword:'New password', confirmNewPassword:'Confirm new password', updatePassword:'Update password',
      deleteAccount:'Delete account', deleteAccountDesc:'Permanent removal of profile and data.',
      help:'Help', contactUs:'Contact us', emailSupport:'Email support', telegramSupport:'Telegram support',
      setupGuide: "Here's your step-by-step guide to setting up your personal bot. Click to get started.",
      academy: "Academy",
      levelBeginner: "Beginner",
      levelIntermediate: "Intermediate",
      academyPrev: "Previous lesson",
      academyNext: "Next lesson",
      academyShowIndex: "Show lessons index ▾",
      academyHideIndex: "Hide lessons index ▴",
      backToTop: "Back to top",
      heroBeginnerTitle: "Forex and market fundamentals.",
      heroBeginnerLead:
        "Collect the basics: how Forex works, what trading really is, the risks involved, and the key concepts you should master before advanced strategies.",
      heroIntermediateTitle: "Read the market beyond the basics.",
      heroIntermediateLead:
        "Focus on market conditions, Fibonacci, patterns, psychology and backtesting. The goal is to turn scattered concepts into a more structured and coherent trading approach."
    },
    it: {
      refresh: 'Aggiorna',
      welcome:'Bentornato',
      earnWithFriends:'Guadagna con gli amici', referralDesc:'Invita amici e guadagna fino al 30% di commissioni', shareLink:'Condividi',
      personalSettings:'Impostazioni', name:'Nome', email:'Email', save:'Salva',
      address:'Indirizzo', city:'Città', zip:'CAP', country:'Paese', state:'Provincia/Regione', phone:'Telefono',
      logout:'Esci', cancel:'Annulla', menu:'Menu',
      guide:'Guida Candy Bot Standard',
      account:'Account', changePassword:'Cambia password', currentPassword:'Password attuale',
      newPassword:'Nuova password', confirmNewPassword:'Conferma nuova password', updatePassword:'Aggiorna password',
      deleteAccount:'Cancella account', deleteAccountDesc:'Rimozione definitiva di profilo e dati.',
      help:'Supporto', contactUs:'Contattaci', emailSupport:'Supporto email', telegramSupport:'Supporto Telegram',
      setupGuide: "Ecco la tua guida passo-passo per configurare il tuo bot personale. Clicca per iniziare.",
      academy: "Academy",
      levelBeginner: "Beginner",
      levelIntermediate: "Intermedio",
      academyPrev: "Lezione precedente",
      academyNext: "Lezione successiva",
      academyShowIndex: "Mostra indice lezioni ▾",
      academyHideIndex: "Nascondi indice lezioni ▴",
      backToTop: "Torna all’inizio",
      heroBeginnerTitle: "Fondamentali del forex e dei mercati.",
      heroBeginnerLead:
        "Qui raccogli le basi: come funziona il mercato Forex, cosa significa fare trading, quali rischi ci sono e quali concetti devi padroneggiare prima di pensare a strategie avanzate.",
      heroIntermediateTitle: "Leggere il mercato oltre le basi.",
      heroIntermediateLead:
        "In questo livello lavori su condizioni di mercato, Fibonacci, pattern, psicologia e backtest. L’obiettivo è trasformare concetti sparsi in un approccio più strutturato e coerente."
    }
  }[lang]), [lang]);

  /* =========================
     ACADEMY CONTENT
     ========================= */

/* =========================
   ACADEMY CONTENT (IT + EN)
   ========================= */

// BEGINNER
const BEGINNER_LEGEND = useMemo(() => ({
  it: [
    "Cos'è il Forex?",
    "Quanti soldi può fare una persona con il trading Forex?",
    "Di quali strumenti tecnici ho bisogno per il trading?",
    "Coppie di valute. Valute di base e quotazione. Major e cross.",
    "Come fare trading?",
    "Cosa sono i pip e i lotti?",
    "Calcolo profitti.",
    "Prezzo Bid e Ask. Spread.",
    "Quando è aperto il mercato Forex?",
    "Come posso prevedere come si muoveranno i tassi di cambio?",
    "Calendario Economico.",
    "Transazione, profitto, perdita. Tipi di ordini.",
    "Swap e Rollover.",
    "Leva e margine.",
    "Come determinare la dimensione della posizione?",
    "Broker Forex.",
    "Conti demo.",
  ],
  en: [
    "What is Forex?",
    "How much money can someone make trading Forex?",
    "What technical tools do I need for trading?",
    "Currency pairs. Base and quote currencies. Majors and crosses.",
    "How do you trade?",
    "What are pips and lots?",
    "Profit calculation.",
    "Bid and Ask price. Spread.",
    "When is the Forex market open?",
    "How can I predict how exchange rates will move?",
    "Economic Calendar.",
    "Trade, profit, loss. Order types.",
    "Swap and Rollover.",
    "Leverage and margin.",
    "How to determine position size?",
    "Forex brokers.",
    "Demo accounts.",
  ],
}), []);

const BEGINNER_LESSONS = useMemo(() => ({
  it: [
    {
      title: "Lezione 1 · Cos’è il Forex?",
      paragraphs: [
        "Il Forex (FOReign EXchange) è il mercato globale in cui una valuta viene scambiata con un’altra. Non è una borsa fisica, ma una rete di banche, istituzioni e trader che comprano e vendono valute 24 ore su 24, 5 giorni su 7.",
        "Operi sempre su coppie (es. EUR/USD): stai comprando una valuta e contemporaneamente vendendo l’altra. Se “comprerai” EUR/USD, stai comprando euro e vendendo dollari; se “venderai” EUR/USD, stai vendendo euro e comprando dollari."
      ],
      muted: "Alla fine di questa lezione dovresti avere chiaro che il Forex è un mercato di scambio di valute e che ogni operazione coinvolge sempre due valute."
    },
    {
      title: "Lezione 2 · Quanti soldi può fare una persona col trading Forex?",
      paragraphs: [
        "Non esiste una cifra standard e non c’è alcuna garanzia di profitto. Il trading non è uno stipendio fisso.",
        "I risultati dipendono da capitale iniziale, gestione del rischio, metodo, disciplina ed esperienza.",
        "Le stesse percentuali di rendimento hanno impatto diverso a seconda del capitale: il 2% su 1.000€ è 20€, il 2% su 50.000€ è 1.000€."
      ],
      bullets: [
        "Capitale iniziale",
        "Gestione del rischio",
        "Metodo e disciplina",
        "Esperienza"
      ],
      muted: "Obiettivo: ridimensionare le aspettative e capire che la priorità è imparare a proteggere il capitale."
    },
    {
      title: "Lezione 3 · Di quali strumenti tecnici ho bisogno per il trading?",
      paragraphs: [
        "Per iniziare hai bisogno di pochi elementi essenziali, non di mille indicatori."
      ],
      bullets: [
        "Un dispositivo e una connessione stabile",
        "Una piattaforma di trading (es. MT4/MT5)",
        "Un broker presso cui aprire un conto demo o reale",
        "Strumenti di analisi di base e calendario economico",
        "Un diario di trading"
      ],
      muted: "Tutto ciò che va oltre ha senso dopo che hai consolidato le basi."
    },
    {
      title: "Lezione 4 · Coppie di valute, base e quotazione, major e cross",
      paragraphs: [
        "Una coppia valutaria è scritta come BASE/QUOTATA, ad esempio EUR/USD.",
        "La valuta base è la prima (EUR). La valuta quotata è la seconda (USD).",
        "Le major sono le coppie più scambiate e tendono ad avere spread più bassi. Le cross non includono USD."
      ],
      bullets: [
        "Valuta base: unità di riferimento",
        "Valuta quotata: prezzo della base",
        "Major: massima liquidità",
        "Cross: senza USD"
      ]
    },
    {
      title: "Lezione 5 · Come fare trading?",
      paragraphs: [
        "Fare trading significa aprire e chiudere posizioni con l’obiettivo di comprare a un prezzo e vendere a un prezzo migliore (o vendere e ricomprare più in basso).",
        "Il flusso base parte dall’analisi del contesto e arriva alla definizione di ingresso, stop-loss, target e size."
      ],
      bullets: [
        "Scegli coppia e timeframe",
        "Analizza il contesto",
        "Definisci ingresso, stop e target",
        "Calcola la size",
        "Esegui e gestisci secondo regole"
      ]
    },
    {
      title: "Lezione 6 · Cosa sono i pip e i lotti?",
      paragraphs: [
        "Un pip è l’unità minima di variazione del prezzo (di solito 0,0001).",
        "Un lotto è la dimensione standard della posizione."
      ],
      bullets: [
        "1 lotto = 100.000 unità della valuta base",
        "0,1 lotto = 10.000",
        "0,01 lotto = 1.000"
      ]
    },
    {
      title: "Lezione 7 · Calcolo dei profitti",
      paragraphs: [
        "Il profitto o la perdita dipende da quanti pip si muove il prezzo e dal valore del pip per la size utilizzata.",
        "Esempio semplificato: se il valore di un pip è 1€ e il prezzo si muove di 20 pip a tuo favore, guadagni 20€."
      ]
    },
    {
      title: "Lezione 8 · Prezzo Bid e Ask, Spread",
      paragraphs: [
        "Il broker mostra sempre due prezzi: Bid (vendita) e Ask (acquisto).",
        "Lo spread è la differenza tra Ask e Bid ed è uno dei costi impliciti del trading."
      ]
    },
    {
      title: "Lezione 9 · Quando è aperto il mercato Forex?",
      paragraphs: [
        "Il Forex è aperto 24 ore su 24, 5 giorni su 7, grazie alla sovrapposizione delle sessioni asiatiche, europee e americane.",
        "Ogni sessione ha caratteristiche diverse di volatilità e liquidità."
      ]
    },
    {
      title: "Lezione 10 · Come prevedere i tassi di cambio?",
      paragraphs: [
        "Non puoi prevedere il futuro con certezza. Puoi formulare ipotesi probabilistiche usando analisi fondamentale, tecnica e sentiment."
      ],
      bullets: [
        "Analisi fondamentale",
        "Analisi tecnica/price action",
        "Sentiment di mercato"
      ]
    },
    {
      title: "Lezione 11 · Calendario Economico",
      paragraphs: [
        "Il calendario economico elenca le principali pubblicazioni di dati macro con data, ora, paese e impatto atteso.",
        "Serve a sapere quando potrebbero esserci forti movimenti e a evitare ingressi impulsivi prima di news ad alta volatilità."
      ]
    },
    {
      title: "Lezione 12 · Transazione, profitto, perdita. Tipi di ordini.",
      paragraphs: [
        "Una transazione è l’apertura e chiusura di una posizione.",
        "I principali ordini includono market, limit, stop, stop-loss e take-profit."
      ],
      bullets: [
        "Market",
        "Limit",
        "Stop",
        "Stop-loss",
        "Take-profit"
      ]
    },
    {
      title: "Lezione 13 · Swap e Rollover",
      paragraphs: [
        "Mantenendo la posizione aperta oltre il rollover giornaliero puoi pagare o ricevere uno swap.",
        "Lo swap riflette la differenza di tassi di interesse e le condizioni del broker."
      ]
    },
    {
      title: "Lezione 14 · Leva e margine",
      paragraphs: [
        "La leva ti permette di controllare una posizione più grande del capitale impegnato.",
        "Il margine è la parte del capitale “bloccata” come garanzia."
      ]
    },
    {
      title: "Lezione 15 · Come determinare la dimensione della posizione?",
      paragraphs: [
        "Parti dal rischio per trade (es. 1% del conto).",
        "In base alla distanza tra ingresso e stop-loss e al valore del pip, calcoli quanti lotti puoi usare."
      ]
    },
    {
      title: "Lezione 16 · Broker Forex",
      paragraphs: [
        "Il broker è l’intermediario attraverso cui accedi al mercato.",
        "Valuta regolamentazione, costi, piattaforme e qualità di esecuzione."
      ],
      bullets: [
        "Regolamentazione",
        "Spread e commissioni",
        "Piattaforme",
        "Condizioni di margine"
      ]
    },
    {
      title: "Lezione 17 · Conti demo",
      paragraphs: [
        "Il conto demo ti permette di fare pratica con denaro virtuale ma condizioni di mercato reali.",
        "È utile per imparare la piattaforma e testare strategie senza rischi emotivi e finanziari."
      ]
    },
  ],

  en: [
    {
      title: "Lesson 1 · What is Forex?",
      paragraphs: [
        "Forex (FOReign EXchange) is the global market where one currency is exchanged for another. It’s not a physical exchange, but a network of banks, institutions and traders buying and selling currencies 24 hours a day, 5 days a week.",
        "You always trade pairs (e.g., EUR/USD): you buy one currency and simultaneously sell the other. If you buy EUR/USD, you are buying euros and selling dollars; if you sell EUR/USD, you are selling euros and buying dollars."
      ],
      muted: "By the end of this lesson you should clearly understand that Forex is a currency exchange market and every trade always involves two currencies."
    },
    {
      title: "Lesson 2 · How much money can someone make trading Forex?",
      paragraphs: [
        "There is no standard number and there is no guaranteed profit. Trading is not a fixed salary.",
        "Results depend on starting capital, risk management, method, discipline and experience.",
        "The same percentage return has a very different impact depending on capital: 2% on €1,000 is €20, while 2% on €50,000 is €1,000."
      ],
      bullets: [
        "Starting capital",
        "Risk management",
        "Method and discipline",
        "Experience"
      ],
      muted: "Goal: set realistic expectations and understand that the priority is learning how to protect your capital."
    },
    {
      title: "Lesson 3 · What technical tools do I need for trading?",
      paragraphs: [
        "To start, you only need a few essentials — not a thousand indicators."
      ],
      bullets: [
        "A device and a stable internet connection",
        "A trading platform (e.g., MT4/MT5)",
        "A broker where you can open a demo or live account",
        "Basic analysis tools and an economic calendar",
        "A trading journal"
      ],
      muted: "Everything beyond that makes sense only after you’ve mastered the basics."
    },
    {
      title: "Lesson 4 · Currency pairs, base & quote, majors and crosses",
      paragraphs: [
        "A currency pair is written as BASE/QUOTE, for example EUR/USD.",
        "The base currency is the first one (EUR). The quote currency is the second one (USD).",
        "Majors are the most traded pairs and tend to have lower spreads. Crosses do not include USD."
      ],
      bullets: [
        "Base currency: the reference unit",
        "Quote currency: the price of the base",
        "Majors: highest liquidity",
        "Crosses: without USD"
      ]
    },
    {
      title: "Lesson 5 · How do you trade?",
      paragraphs: [
        "Trading means opening and closing positions with the goal of buying at one price and selling at a better one (or selling first and buying back lower).",
        "The basic workflow starts from context analysis and ends with defining entry, stop-loss, target and position size."
      ],
      bullets: [
        "Choose pair and timeframe",
        "Analyze the context",
        "Define entry, stop and target",
        "Calculate position size",
        "Execute and manage with rules"
      ]
    },
    {
      title: "Lesson 6 · What are pips and lots?",
      paragraphs: [
        "A pip is the smallest unit of price movement (usually 0.0001).",
        "A lot is the standard position size."
      ],
      bullets: [
        "1 lot = 100,000 units of the base currency",
        "0.1 lot = 10,000",
        "0.01 lot = 1,000"
      ]
    },
    {
      title: "Lesson 7 · Profit calculation",
      paragraphs: [
        "Profit or loss depends on how many pips price moves and the pip value for the position size used.",
        "Simplified example: if pip value is €1 and price moves 20 pips in your favor, you gain €20."
      ]
    },
    {
      title: "Lesson 8 · Bid and Ask price, Spread",
      paragraphs: [
        "Your broker always shows two prices: Bid (sell) and Ask (buy).",
        "The spread is the difference between Ask and Bid and it’s one of the implicit trading costs."
      ]
    },
    {
      title: "Lesson 9 · When is the Forex market open?",
      paragraphs: [
        "Forex is open 24 hours a day, 5 days a week, thanks to the overlap of Asian, European and US sessions.",
        "Each session has different volatility and liquidity characteristics."
      ]
    },
    {
      title: "Lesson 10 · How can exchange rates be predicted?",
      paragraphs: [
        "You can’t predict the future with certainty. You can form probabilistic scenarios using fundamental analysis, technical analysis and sentiment."
      ],
      bullets: [
        "Fundamental analysis",
        "Technical analysis / price action",
        "Market sentiment"
      ]
    },
    {
      title: "Lesson 11 · Economic Calendar",
      paragraphs: [
        "The economic calendar lists major macroeconomic releases with date, time, country and expected impact.",
        "It helps you know when strong moves may happen and avoid impulsive entries right before high-volatility news."
      ]
    },
    {
      title: "Lesson 12 · Trade, profit, loss. Order types.",
      paragraphs: [
        "A trade is the opening and closing of a position.",
        "Main order types include market, limit, stop, stop-loss and take-profit."
      ],
      bullets: [
        "Market",
        "Limit",
        "Stop",
        "Stop-loss",
        "Take-profit"
      ]
    },
    {
      title: "Lesson 13 · Swap and Rollover",
      paragraphs: [
        "If you keep a position open past the daily rollover time, you may pay or receive a swap.",
        "The swap reflects interest rate differences and broker conditions."
      ]
    },
    {
      title: "Lesson 14 · Leverage and margin",
      paragraphs: [
        "Leverage lets you control a larger position than the capital you commit.",
        "Margin is the portion of capital “locked” as collateral."
      ]
    },
    {
      title: "Lesson 15 · How to determine position size?",
      paragraphs: [
        "Start from your risk per trade (e.g., 1% of the account).",
        "Based on the distance between entry and stop-loss and the pip value, you calculate how many lots you can use."
      ]
    },
    {
      title: "Lesson 16 · Forex brokers",
      paragraphs: [
        "A broker is the intermediary that gives you access to the market.",
        "Evaluate regulation, costs, platforms and execution quality."
      ],
      bullets: [
        "Regulation",
        "Spreads and commissions",
        "Platforms",
        "Margin conditions"
      ]
    },
    {
      title: "Lesson 17 · Demo accounts",
      paragraphs: [
        "A demo account lets you practice with virtual money under real market conditions.",
        "It’s useful to learn the platform and test strategies without emotional and financial risk."
      ]
    },
  ]
}), []);

// INTERMEDIATE
const INTERMEDIATE_LEGEND = useMemo(() => ({
  it: [
    "Condizioni e fasi del mercato",
    "Range",
    "Trends",
    "Come individuare un’inversione del mercato",
    "Candele giapponesi",
    "Psicologia",
    "Cosa sono gli strumenti di Fibonacci?",
    "Come usare i ritracciamenti di Fibonacci",
    "Fan Lines di Fibonacci",
    "Espansioni di Fibonacci",
    "Stili di trading",
    "Position trading",
    "Lo scalping",
    "Trend trading",
    "Swing trading",
    "Carry trade",
    "Day trading",
    "Pattern di candele",
    "Pattern di continuazione con candele",
    "Pattern di inversione con candele",
    "Come creare la propria strategia di trading?",
    "Alla scoperta degli indicatori di Gann",
    "Pattern grafici",
    "Teoria di Bill Williams",
    "Awesome Oscillator",
    "Oscillatore Gator",
    "Frattali",
    "Alligatore",
    "Market Facilitation Index",
    "Come fare il backtest di una strategia di trading",
    "Come gestire il rumore del mercato?",
  ],
  en: [
    "Market conditions and phases",
    "Range",
    "Trends",
    "How to spot a market reversal",
    "Japanese candlesticks",
    "Psychology",
    "What are Fibonacci tools?",
    "How to use Fibonacci retracements",
    "Fibonacci Fan Lines",
    "Fibonacci extensions",
    "Trading styles",
    "Position trading",
    "Scalping",
    "Trend trading",
    "Swing trading",
    "Carry trade",
    "Day trading",
    "Candlestick patterns",
    "Candlestick continuation patterns",
    "Candlestick reversal patterns",
    "How to build your own trading strategy",
    "Discovering Gann indicators",
    "Chart patterns",
    "Bill Williams theory",
    "Awesome Oscillator",
    "Gator Oscillator",
    "Fractals",
    "Alligator",
    "Market Facilitation Index",
    "How to backtest a trading strategy",
    "How to manage market noise",
  ],
}), []);

const INTERMEDIATE_LESSONS = useMemo(() => ({
  it: [
    {
      title: "Lezione 1 · Condizioni e fasi del mercato",
      paragraphs: [
        "Il mercato non si muove sempre allo stesso modo. Alterna fasi di trend, range, forte volatilità e fasi di “sonno”. Ogni fase favorisce certe strategie e ne penalizza altre.",
        "L’idea chiave è imparare a chiederti: “In che tipo di fase mi trovo adesso?” prima di applicare una tecnica.",
        "Molte perdite non arrivano da “strategie sbagliate”, ma da strategie giuste usate nel contesto sbagliato."
      ]
    },
    {
      title: "Lezione 2 · Range",
      paragraphs: [
        "Un range è una fase in cui il prezzo si muove tra un’area di supporto e una di resistenza senza una direzione chiara.",
        "In queste condizioni funzionano meglio strategie di mean reversion (comprare vicino al supporto e vendere vicino alla resistenza).",
        "Le logiche di breakout richiedono maggiori conferme di volume e volatilità."
      ]
    },
    {
      title: "Lezione 3 · Trends",
      paragraphs: [
        "Un trend è una sequenza di massimi e minimi che si muovono in modo coerente."
      ],
      bullets: [
        "Trend rialzista: massimi e minimi crescenti.",
        "Trend ribassista: massimi e minimi decrescenti."
      ],
      muted: "L’obiettivo non è “prendere il minimo assoluto”, ma allinearsi al movimento finché resta valido."
    },
    {
      title: "Lezione 4 · Come individuare un’inversione del mercato",
      paragraphs: [
        "Un’inversione non è una singola candela magica, ma spesso un mix di elementi: perdita della struttura del trend, rottura di livelli chiave e aumenti di volatilità.",
        "In un trend rialzista, se il prezzo rompe un minimo importante e non riesce più a fare nuovi massimi, potresti essere in una fase di distribuzione o inizio di trend ribassista."
      ],
      muted: "L’idea non è anticipare sempre, ma riconoscere quando la struttura smette di reggere."
    },
    {
      title: "Lezione 5 · Candele giapponesi",
      paragraphs: [
        "Le candele giapponesi mostrano apertura, massimo, minimo e chiusura di un periodo.",
        "Il corpo racconta dove si è concentrata l’azione, le ombre mostrano fin dove si è spinto il prezzo.",
        "Osservare la forma delle candele aiuta a capire la battaglia tra compratori e venditori, soprattutto su livelli chiave."
      ]
    },
    {
      title: "Lezione 6 · Psicologia",
      paragraphs: [
        "Nel trading non esiste solo il setup tecnico: ci sei tu.",
        "Paura, avidità, ansia da recupero e bisogno di avere ragione possono sabotare anche la strategia meglio progettata."
      ],
      bullets: [
        "Come reagisci a una serie di perdite.",
        "Cosa succede quando sei molto in profitto.",
        "Se rispetti davvero stop e regole di size."
      ],
      muted: "Tenere un diario emotivo oltre che tecnico è un’abitudine molto utile."
    },
    {
      title: "Lezione 7 · Cosa sono gli strumenti di Fibonacci?",
      paragraphs: [
        "Gli strumenti di Fibonacci (ritracciamenti, estensioni, fan, ecc.) si basano su rapporti matematici ricorrenti usati per stimare potenziali aree di ritracciamento o estensione del prezzo.",
        "Non sono livelli “magici”, ma aiutano a dare struttura a concetti come ritracciamenti sani o estensioni eccessive."
      ]
    },
    {
      title: "Lezione 8 · Come usare i ritracciamenti di Fibonacci",
      paragraphs: [
        "I ritracciamenti di Fibonacci si tracciano tra un massimo e un minimo significativi per evidenziare aree dove il prezzo potrebbe fermarsi prima di riprendere il trend principale.",
        "Molti trader osservano in particolare le zone 38,2% – 50% – 61,8% come aree di interesse, soprattutto se coincidono con supporti/resistenze e altre conferme."
      ]
    },
    {
      title: "Lezione 9 · Fan Lines di Fibonacci",
      paragraphs: [
        "Le Fan Lines di Fibonacci sono linee diagonali basate sui rapporti di Fibonacci che rappresentano possibili supporti o resistenze dinamici.",
        "Sono utili per ragionare sull’inclinazione del trend e capire quando il mercato rallenta o accelera."
      ]
    },
    {
      title: "Lezione 10 · Espansioni di Fibonacci",
      paragraphs: [
        "Le espansioni di Fibonacci servono a proiettare possibili zone di target oltre l’ultimo massimo/minimo conosciuto.",
        "Possono aiutarti a definire aree di take-profit più sensate nei trend."
      ]
    },
    {
      title: "Lezione 11 · Stili di trading",
      paragraphs: [
        "Ogni stile di trading ha un suo ritmo, orizzonte temporale e profilo psicologico richiesto.",
        "Non esiste uno stile migliore in assoluto: esiste quello più compatibile con te."
      ]
    },
    {
      title: "Lezione 12 · Position trading",
      paragraphs: [
        "Il position trading mantiene posizioni per settimane o mesi, cercando di cavalcare grandi movimenti macro o di lungo periodo.",
        "Richiede pazienza e buona comprensione del contesto fondamentale, oltre a gestione attenta di swap e leva."
      ]
    },
    {
      title: "Lezione 13 · Lo scalping",
      paragraphs: [
        "Lo scalping cerca piccoli movimenti sul breve termine con molti trade e target stretti.",
        "Richiede esecuzione rapida, costi bassi e forte controllo emotivo."
      ],
      muted: "Non è adatto a tutti: è uno degli stili più stressanti."
    },
    {
      title: "Lezione 14 · Trend trading",
      paragraphs: [
        "Il trend trading mira a entrare nella direzione del trend e rimanere in posizione finché il trend resta intatto.",
        "È spesso combinato con medie mobili, Fibonacci e livelli di supporto/resistenza."
      ]
    },
    {
      title: "Lezione 15 · Swing trading",
      paragraphs: [
        "Lo swing trading punta a catturare le oscillazioni all’interno di un movimento più ampio.",
        "L’orizzonte è spesso di qualche giorno ed è un buon compromesso per chi lavora."
      ]
    },
    {
      title: "Lezione 16 · Carry trade",
      paragraphs: [
        "Il carry trade sfrutta la differenza di tassi di interesse tra due valute puntando allo swap positivo.",
        "Espone anche al rischio di movimenti forti di cambio."
      ]
    },
    {
      title: "Lezione 17 · Day trading",
      paragraphs: [
        "Il day trading prevede apertura e chiusura delle posizioni nella stessa giornata.",
        "Riduce il rischio legato alle news fuori orario ma richiede presenza e piano intraday chiaro."
      ]
    },
    {
      title: "Lezione 18 · Pattern di candele",
      paragraphs: [
        "I pattern di candele sono combinazioni che rappresentano interazioni tra compratori e venditori (doji, engulfing, pin bar, ecc.).",
        "Diventano potenti se letti in contesto: livelli chiave, trend e conferme."
      ]
    },
    {
      title: "Lezione 19 · Pattern di continuazione con candele",
      paragraphs: [
        "I pattern di continuazione suggeriscono che, dopo una pausa, il trend dominante potrebbe riprendere.",
        "L’idea è evitare di uscire troppo presto da movimenti sani."
      ]
    },
    {
      title: "Lezione 20 · Pattern di inversione con candele",
      paragraphs: [
        "I pattern di inversione segnalano possibili cambi di direzione.",
        "Non sono garanzie ma segnali di attenzione per rivalutare la struttura del trend."
      ]
    },
    {
      title: "Lezione 21 · Come creare la propria strategia di trading?",
      paragraphs: [
        "Una strategia non è solo un pattern di ingresso. Include contesto, regole di entrata/uscita, gestione del rischio e quando non operare."
      ],
      bullets: [
        "Contesto operativo",
        "Regole di ingresso/uscita",
        "Gestione del rischio e della size",
        "Gestione della posizione",
        "Regole di esclusione"
      ],
      muted: "Scrivere la strategia è il primo passo per misurarla e migliorarla."
    },
    {
      title: "Lezione 22 · Alla scoperta degli indicatori di Gann",
      paragraphs: [
        "Gli strumenti di Gann combinano prezzo e tempo con angoli e griglie (1x1, 2x1, ecc.).",
        "A livello intermedio l’obiettivo è capire che esistono metodologie che leggono anche il fattore tempo."
      ]
    },
    {
      title: "Lezione 23 · Pattern grafici",
      paragraphs: [
        "I pattern grafici (testa e spalle, doppi massimi/minimi, triangoli, flag, canali) rappresentano fasi di accumulazione, distribuzione o continuazione.",
        "L’importante è capirne la logica di fondo, non solo la forma."
      ]
    },
    {
      title: "Lezione 24 · Teoria di Bill Williams",
      paragraphs: [
        "Bill Williams ha sviluppato concetti e indicatori per descrivere il “caos organizzato” del mercato.",
        "L’idea è riconoscere quando il mercato dorme, si sveglia o entra in trend."
      ]
    },
    {
      title: "Lezione 25 · Awesome Oscillator",
      paragraphs: [
        "L’Awesome Oscillator misura il momentum confrontando due medie mobili semplici sul median price.",
        "Può aiutare a individuare cambi di momentum e divergenze."
      ]
    },
    {
      title: "Lezione 26 · Oscillatore Gator",
      paragraphs: [
        "Il Gator Oscillator lavora con l’Alligatore di Williams, evidenziando visivamente gli stati di sonno/sveglia del trend.",
        "Serve a capire quando il trend è forte o quando sta perdendo energia."
      ]
    },
    {
      title: "Lezione 27 · Frattali",
      paragraphs: [
        "I frattali segnano punti di massimo e minimo locali e possono essere usati come riferimenti di livello per ingressi o stop.",
        "Aiutano a individuare zone di rottura o difesa."
      ]
    },
    {
      title: "Lezione 28 · Alligatore",
      paragraphs: [
        "L’Alligatore è composto da tre medie mobili smoothed e aiuta a visualizzare quando il mercato è in trend o in congestione.",
        "È utile come filtro di contesto."
      ]
    },
    {
      title: "Lezione 29 · Market Facilitation Index",
      paragraphs: [
        "Il Market Facilitation Index misura quanto il prezzo si muove rispetto al volume.",
        "Combinato ai volumi può indicare se un movimento è sostenuto o fragile."
      ]
    },
    {
      title: "Lezione 30 · Come fare il backtest di una strategia di trading",
      paragraphs: [
        "Il backtest consiste nell’applicare la strategia a dati storici per valutare come avrebbe performato.",
        "L’obiettivo non è trovare la strategia perfetta, ma capire se l’idea ha senso e dove è fragile."
      ],
      bullets: [
        "Regole chiare e non ambigue",
        "Periodo storico rappresentativo",
        "Registrazione di tutti i trade",
        "Metriche: win rate, payoff, drawdown, ecc."
      ]
    },
    {
      title: "Lezione 31 · Come gestire il rumore del mercato?",
      paragraphs: [
        "Il rumore è l’insieme di piccoli movimenti casuali che possono indurti a decisioni impulsive.",
        "A livello intermedio impari che meno è meglio: meno rumore, più coerenza."
      ],
      bullets: [
        "Usare timeframe coerenti con il tuo stile",
        "Stop in zone logiche, non attaccati al prezzo",
        "Evitare di guardare ogni tick se la strategia è più ampia"
      ]
    }
  ],

  // EN version (same amount of lessons, aligned by index)
  en: [
    {
      title: "Lesson 1 · Market conditions and phases",
      paragraphs: [
        "The market doesn’t move the same way all the time. It alternates between trending phases, ranging phases, high volatility and “sleepy” phases. Each phase favors certain strategies and punishes others.",
        "The key idea is to ask yourself: “What phase am I in right now?” before applying a technique.",
        "Many losses don’t come from “bad strategies”, but from good strategies used in the wrong context."
      ]
    },
    {
      title: "Lesson 2 · Range",
      paragraphs: [
        "A range is a phase where price moves between a support area and a resistance area with no clear direction.",
        "In these conditions, mean-reversion strategies often work best (buy near support and sell near resistance).",
        "Breakout approaches need stronger confirmations from volatility and momentum."
      ]
    },
    {
      title: "Lesson 3 · Trends",
      paragraphs: [
        "A trend is a sequence of highs and lows moving in a consistent direction."
      ],
      bullets: [
        "Uptrend: higher highs and higher lows.",
        "Downtrend: lower highs and lower lows."
      ],
      muted: "The goal is not to catch the absolute bottom/top, but to align with the move while it remains valid."
    },
    {
      title: "Lesson 4 · How to spot a market reversal",
      paragraphs: [
        "A reversal is not a single magic candle; it’s often a combination of elements: loss of trend structure, breaks of key levels and volatility expansion.",
        "In an uptrend, if price breaks an important swing low and fails to print new highs, you might be seeing distribution or the start of a downtrend."
      ],
      muted: "The idea is not to always anticipate — it’s to recognize when structure stops holding."
    },
    {
      title: "Lesson 5 · Japanese candlesticks",
      paragraphs: [
        "Japanese candlesticks show the open, high, low and close of a period.",
        "The body tells you where most activity occurred; the wicks show how far price pushed.",
        "Reading candle shapes helps you understand the battle between buyers and sellers, especially around key levels."
      ]
    },
    {
      title: "Lesson 6 · Psychology",
      paragraphs: [
        "In trading there isn’t only the technical setup — there’s you.",
        "Fear, greed, revenge trading and the need to be right can sabotage even the best-designed strategy."
      ],
      bullets: [
        "How you react to a losing streak.",
        "What happens when you’re strongly in profit.",
        "Whether you truly respect stops and position sizing rules."
      ],
      muted: "Keeping an emotional journal (not only a technical one) is a very useful habit."
    },
    {
      title: "Lesson 7 · What are Fibonacci tools?",
      paragraphs: [
        "Fibonacci tools (retracements, extensions, fans, etc.) are based on recurring mathematical ratios used to estimate potential retracement or expansion areas.",
        "They are not “magic levels”, but they add structure to concepts like healthy pullbacks or overstretched moves."
      ]
    },
    {
      title: "Lesson 8 · How to use Fibonacci retracements",
      paragraphs: [
        "Fibonacci retracements are drawn between a significant high and low to highlight areas where price may pause before resuming the main trend.",
        "Many traders focus on 38.2% – 50% – 61.8% zones, especially when they align with support/resistance and other confirmations."
      ]
    },
    {
      title: "Lesson 9 · Fibonacci Fan Lines",
      paragraphs: [
        "Fibonacci Fan Lines are diagonal lines based on Fibonacci ratios that act as potential dynamic support/resistance.",
        "They’re useful for reasoning about trend slope and whether the market is slowing down or accelerating."
      ]
    },
    {
      title: "Lesson 10 · Fibonacci extensions",
      paragraphs: [
        "Fibonacci extensions project potential target areas beyond the most recent high/low.",
        "They can help define more logical take-profit areas during trends."
      ]
    },
    {
      title: "Lesson 11 · Trading styles",
      paragraphs: [
        "Each trading style has its own rhythm, time horizon and psychological demands.",
        "There is no universally best style — there is only the style that best fits you."
      ]
    },
    {
      title: "Lesson 12 · Position trading",
      paragraphs: [
        "Position trading holds trades for weeks or months, aiming to ride large macro/long-term moves.",
        "It requires patience and good understanding of fundamentals, plus careful leverage and swap management."
      ]
    },
    {
      title: "Lesson 13 · Scalping",
      paragraphs: [
        "Scalping targets small short-term moves with many trades and tight targets.",
        "It requires fast execution, low costs and strong emotional control."
      ],
      muted: "Not for everyone: it’s one of the most stressful styles."
    },
    {
      title: "Lesson 14 · Trend trading",
      paragraphs: [
        "Trend trading aims to enter in the trend direction and stay in the trade while the trend remains intact.",
        "It’s often combined with moving averages, Fibonacci and support/resistance levels."
      ]
    },
    {
      title: "Lesson 15 · Swing trading",
      paragraphs: [
        "Swing trading aims to capture swings within a broader move.",
        "The horizon is usually a few days and it’s a good compromise for people who work."
      ]
    },
    {
      title: "Lesson 16 · Carry trade",
      paragraphs: [
        "Carry trade seeks to benefit from interest rate differentials between two currencies through positive swap.",
        "It also exposes you to strong exchange-rate moves."
      ]
    },
    {
      title: "Lesson 17 · Day trading",
      paragraphs: [
        "Day trading opens and closes positions within the same day.",
        "It reduces overnight news risk, but requires presence and a clear intraday plan."
      ]
    },
    {
      title: "Lesson 18 · Candlestick patterns",
      paragraphs: [
        "Candlestick patterns are combinations representing interactions between buyers and sellers (doji, engulfing, pin bar, etc.).",
        "They become powerful when read in context: key levels, trend and confirmations."
      ]
    },
    {
      title: "Lesson 19 · Candlestick continuation patterns",
      paragraphs: [
        "Continuation patterns suggest that, after a pause, the dominant trend may resume.",
        "The idea is to avoid exiting too early from healthy moves."
      ]
    },
    {
      title: "Lesson 20 · Candlestick reversal patterns",
      paragraphs: [
        "Reversal patterns signal potential direction changes.",
        "They are not guarantees, but warning signs to re-evaluate structure."
      ]
    },
    {
      title: "Lesson 21 · How to build your own trading strategy",
      paragraphs: [
        "A strategy is not just an entry pattern. It includes context, entry/exit rules, risk management and when NOT to trade."
      ],
      bullets: [
        "Operating context",
        "Entry/exit rules",
        "Risk and position sizing",
        "Trade management",
        "Exclusion rules"
      ],
      muted: "Writing your strategy is the first step to measuring and improving it."
    },
    {
      title: "Lesson 22 · Discovering Gann indicators",
      paragraphs: [
        "Gann tools combine price and time using angles and grids (1x1, 2x1, etc.).",
        "At the intermediate level, the goal is to understand that some methodologies also read the time factor."
      ]
    },
    {
      title: "Lesson 23 · Chart patterns",
      paragraphs: [
        "Chart patterns (head and shoulders, double tops/bottoms, triangles, flags, channels) represent accumulation, distribution or continuation phases.",
        "What matters is understanding the underlying logic, not just the shape."
      ]
    },
    {
      title: "Lesson 24 · Bill Williams theory",
      paragraphs: [
        "Bill Williams developed concepts and indicators to describe the market’s “organized chaos”.",
        "The idea is to recognize when the market sleeps, wakes up or enters a trend."
      ]
    },
    {
      title: "Lesson 25 · Awesome Oscillator",
      paragraphs: [
        "Awesome Oscillator measures momentum by comparing two simple moving averages of the median price.",
        "It can help spot momentum shifts and divergences."
      ]
    },
    {
      title: "Lesson 26 · Gator Oscillator",
      paragraphs: [
        "Gator Oscillator works with Williams’ Alligator, visually highlighting sleep/awake phases of the trend.",
        "It helps you understand when a trend is strong or losing energy."
      ]
    },
    {
      title: "Lesson 27 · Fractals",
      paragraphs: [
        "Fractals mark local highs and lows and can be used as level references for entries or stops.",
        "They help identify breakout or defense zones."
      ]
    },
    {
      title: "Lesson 28 · Alligator",
      paragraphs: [
        "The Alligator is made of three smoothed moving averages and helps visualize when the market is trending or consolidating.",
        "It’s useful as a context filter."
      ]
    },
    {
      title: "Lesson 29 · Market Facilitation Index",
      paragraphs: [
        "The Market Facilitation Index measures how much price moves relative to volume.",
        "Combined with volume, it may indicate whether a move is supported or fragile."
      ]
    },
    {
      title: "Lesson 30 · How to backtest a trading strategy",
      paragraphs: [
        "Backtesting means applying a strategy to historical data to evaluate how it would have performed.",
        "The goal is not to find the perfect strategy, but to understand whether the idea makes sense and where it’s fragile."
      ],
      bullets: [
        "Clear, unambiguous rules",
        "A representative historical period",
        "Recording every trade",
        "Metrics: win rate, payoff, drawdown, etc."
      ]
    },
    {
      title: "Lesson 31 · How to manage market noise",
      paragraphs: [
        "Noise is the set of small random moves that can push you into impulsive decisions.",
        "At an intermediate level you learn that less is more: less noise, more consistency."
      ],
      bullets: [
        "Use timeframes consistent with your style",
        "Place stops in logical zones, not glued to price",
        "Avoid watching every tick if your strategy is broader"
      ]
    }
  ]
}), []);


  // INTERMEDIATE (dal tuo HTML)


  const scrollAcademyAnchor = () => {
    try {
      const el = document.getElementById("academy-lessons-anchor");
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch {}
  };

const getActiveLegend = () => {
  const src = academyLevel === "beginner" ? BEGINNER_LEGEND : INTERMEDIATE_LEGEND;
  return src[lang] || src.en;
};

const getActiveLessons = () => {
  const src = academyLevel === "beginner" ? BEGINNER_LESSONS : INTERMEDIATE_LESSONS;
  return src[lang] || src.en;
};


  const getActiveIndex = () => academyLevel === 'beginner' ? academyBeginnerIndex : academyIntermediateIndex;
  const setActiveIndex = (updater) => {
    if (academyLevel === 'beginner') setAcademyBeginnerIndex(updater);
    else setAcademyIntermediateIndex(updater);
  };

  /* Modal Guida */
  const renderGuideModal = () => (
    <Modal
      open={guideOpen}
      onCancel={() => setGuideOpen(false)}
      footer={null}
      title={
        guideType === 'standard'
          ? (lang === 'it' ? 'Guida Candy Bot Standard' : 'Candy Bot Standard Guide')
          : (lang === 'it' ? 'Configurazione Candy Bot Pro' : 'Candy Bot Pro Setup')
      }
      width="calc(100vw - 8px)"
      style={{ top: 4 }}
      destroyOnClose
      styles={{ content: { borderRadius: 10, overflow: 'hidden', padding: 0 }, body: { padding: 0, height: 'calc(100vh - 56px)', background: '#0f172a' } }}
    >
      <iframe
        title="Candy Bot Guide"
        srcDoc={guideType === 'standard' ? GUIDE_HTML_STANDARD : GUIDE_HTML_SETUP}
        style={{ width: '100%', height: '100%', border: 0 }}
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
      />
    </Modal>
  );

  const renderDashboard = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <motion.div
        initial="offscreen"
        animate="onscreen"
        variants={cardVariants}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexDirection: 'row',
          flexWrap: isMobile ? 'wrap' : 'nowrap'
        }}
      >
        <Space align="center" style={{ flex: 1, minWidth: 0 }}>
          <img src="/logo-st.png" alt="Logo" style={{ height: isSmallMobile ? 24 : 28, width: 'auto' }} />
          <div>
            <Title level={isSmallMobile ? 5 : 4} style={{ margin: 0, color: token.colorText, textAlign: 'center' }}>Dashboard</Title>
            <Text style={{ color: token.colorTextSecondary, fontSize: isSmallMobile ? 12 : 14 }}>{t.welcome}</Text>
          </div>
        </Space>

        <Space wrap align="center" style={{ justifyContent: 'flex-end' }}>
          {isMobile && (
            <Segmented
              size={isSmallMobile ? "small" : "middle"}
              options={[
                { label: '🇬🇧 EN', value: 'en' },
                { label: '🇮🇹 IT', value: 'it' }
              ]}
              value={lang}
              onChange={setLang}
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.2)'
              }}
            />
          )}

          <Tooltip title={lang === 'it' ? 'Aggiorna' : 'Refresh'}>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
              shape="circle"
              type="text"
              style={{ color: token.colorText }}
              size={isSmallMobile ? "small" : "middle"}
            />
          </Tooltip>
        </Space>
      </motion.div>

      <motion.div initial="offscreen" animate="onscreen" variants={cardVariants} transition={{ delay: 0.1 }}>
        <Card className="profile-card-light" style={{ borderRadius: 12, border: '1px solid rgba(17,17,17,0.08)', background: '#ffffff', overflow: 'hidden' }} bodyStyle={{ padding: isSmallMobile ? 12 : 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar size={isSmallMobile ? 40 : 48} icon={<UserOutlined />} style={{ background: token.colorPrimary, color: '#0b0f1a', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Title level={isSmallMobile ? 5 : 4} style={{ margin: 0, color: '#111', fontSize: isSmallMobile ? 14 : 16 }}>{user?.displayName || 'User'}</Title>
              <Paragraph style={{ margin: 0, color: '#444', fontSize: isSmallMobile ? 11 : 13, lineHeight: 1.3 }} ellipsis={{ rows: 1 }}>{user?.email}</Paragraph>
              <div style={{ marginTop: 6 }}>
                {user?.emailVerified
                  ? <Badge status="success" text={<Text style={{ color: '#111', fontSize: isSmallMobile ? 11 : 12 }}>{lang === 'it' ? 'Account verificato' : 'Verified account'}</Text>} />
                  : <Badge status="warning" text={<Text style={{ color: '#111', fontSize: isSmallMobile ? 11 : 12 }}>{lang === 'it' ? 'Verifica in sospeso' : 'Verification pending'}</Text>} />}
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

     

      <motion.div initial="offscreen" animate="onscreen" variants={cardVariants} transition={{ delay: 0.3 }}>
        <Paragraph
          style={{
            color: '#e5e7eb',
            marginBottom: 8,
            textAlign: 'center',
            fontSize: isSmallMobile ? 12 : 14
          }}
        >
          {t.setupGuide}
        </Paragraph>

        <Collapse
          ghost
          expandIcon={() => null}
          expandIconPosition="end"
          items={[{
            key: 'guide',
            showArrow: false,
            label: (
              <div
                onClick={() => { setGuideType('standard'); setGuideOpen(true); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: isSmallMobile ? '10px 12px' : '12px 16px',
                  border: '1px solid rgba(255,255,255,0.95)',
                  borderRadius: 9999,
                  color: '#fff',
                  fontWeight: 700,
                  textAlign: 'center',
                  background: 'transparent',
                  boxShadow: '0 0 0 1px rgba(255,255,255,0.2) inset',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <ThunderboltFilled style={{ fontSize: 18 }} />
                <span style={{ letterSpacing: 0.4 }}>{t.guide}</span>
              </div>
            ),
            children: null
          }]}
        />
      </motion.div>

      <AnimatePresence />
    </div>
  );

  /* =========================
     ACADEMY RENDER (Beginner + Intermediate)
     ========================= */
  const renderAcademy = () => {
    const legend = getActiveLegend();
    const lessons = getActiveLessons();
    const total = lessons.length;
    const idx = getActiveIndex();
    const current = lessons[idx];

    const heroTitle = academyLevel === 'beginner' ? t.heroBeginnerTitle : t.heroIntermediateTitle;
    const heroLead = academyLevel === 'beginner' ? t.heroBeginnerLead : t.heroIntermediateLead;

    const renderLessonBody = (lesson) => (
      <>
        {lesson?.paragraphs?.map((p, i) => <p key={i}>{p}</p>)}
        {lesson?.bullets?.length ? (
          <ul className="academy-list">
            {lesson.bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        ) : null}
        {lesson?.muted ? <p className="academy-muted">{lesson.muted}</p> : null}
      </>
    );

    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
        <style>{`
          .academy-hero {
            position: relative;
            border-radius: 14px;
            overflow: hidden;
            padding: 26px 18px 22px 18px;
            border: 1px solid rgba(255,255,255,0.08);
            background:
              radial-gradient(900px 300px at 10% 10%, rgba(99,102,241,0.18), transparent 60%),
              radial-gradient(900px 300px at 90% 20%, rgba(236,72,153,0.14), transparent 60%),
              linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02));
          }
          .academy-level-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            flex-wrap: wrap;
            margin-bottom: 10px;
          }
          .academy-eyebrow {
            letter-spacing: .12em;
            text-transform: uppercase;
            font-size: 11px;
            opacity: .75;
          }
          .academy-hero h1 {
            margin: 0 0 10px 0;
            font-size: clamp(22px, 2.2vw, 30px);
            font-weight: 800;
          }
          .academy-lead {
            margin: 0;
            opacity: .85;
            font-size: 13.5px;
            line-height: 1.45;
            max-width: 72ch;
          }
          .academy-card {
            border-radius: 12px;
            border: 1px solid rgba(255,255,255,0.08);
            background: rgba(255,255,255,0.04);
            padding: 14px 14px;
          }
          .academy-list {
            padding-left: 18px;
            margin: 8px 0 12px 0;
          }
          .academy-list li { margin: 4px 0; }
          .academy-muted {
            opacity: .72;
            font-size: 12.5px;
          }
          .academy-nav {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            flex-wrap: wrap;
          }
          .academy-counter {
            font-size: 11.5px;
            opacity: .85;
            padding: 4px 10px;
            border-radius: 999px;
            border: 1px solid rgba(255,255,255,0.10);
            background: rgba(255,255,255,0.04);
            max-width: 100%;
          }
          .academy-lesson-title {
            margin: 0 0 8px 0;
            font-size: 16px;
            font-weight: 700;
          }
        `}</style>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Hero + selector livello */}
          <div className="academy-hero">
            <div className="academy-level-row">
              <div className="academy-eyebrow">
                {lang === "it" ? "Formazione · Academy" : "Education · Academy"}
              </div>

              <Segmented
                size="small"
                value={academyLevel}
                onChange={(val) => {
                  setAcademyLevel(val);
                  setAcademyLegendOpen(false);
                  // facoltativo: scroll all'inizio quando cambi livello
                  try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
                }}
                options={[
                  { label: t.levelBeginner, value: "beginner" },
                  { label: t.levelIntermediate, value: "intermediate" }
                ]}
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.18)'
                }}
              />
            </div>

            <h1>{heroTitle}</h1>
            <p className="academy-lead">{heroLead}</p>
          </div>

          {/* Indice a tendina */}
          <div className="academy-card">
            <Button
              size="small"
              onClick={() => setAcademyLegendOpen(s => !s)}
            >
              {academyLegendOpen ? t.academyHideIndex : t.academyShowIndex}
            </Button>

            {academyLegendOpen && (
              <ol className="academy-list" style={{ marginTop: 10 }}>
                {legend.map((q, i) => <li key={i}>{q}</li>)}
              </ol>
            )}
          </div>

          <div id="academy-lessons-anchor" />

          {/* Lezione + frecce */}
          <div className="academy-card">
            <div className="academy-nav">
              <Button
                size="small"
                onClick={() => {
                  if (idx > 0) {
                    setActiveIndex(i => i - 1);
                    scrollAcademyAnchor();
                  }
                }}
                disabled={idx === 0}
              >
                ← {t.academyPrev}
              </Button>

              <span className="academy-counter">
                {lang === "it"
                  ? `Lezione ${idx + 1} di ${total} · ${current?.title}`
                  : `Lesson ${idx + 1} of ${total} · ${current?.title}`}
              </span>

              <Button
                size="small"
                onClick={() => {
                  if (idx < total - 1) {
                    setActiveIndex(i => i + 1);
                    scrollAcademyAnchor();
                  }
                }}
                disabled={idx === total - 1}
              >
                {t.academyNext} →
              </Button>
            </div>

            <div style={{ marginTop: 14, fontSize: 13.5, lineHeight: 1.5 }}>
              <h3 className="academy-lesson-title">{current?.title}</h3>
              {renderLessonBody(current)}
            </div>
          </div>

          {/* Back to top */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button
              size="small"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              ↑ {t.backToTop}
            </Button>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderSettings = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <Card style={{ padding: isSmallMobile ? 12 : 16 }}>
        <Title level={4} style={{ marginTop: 0, color: token.colorText, fontSize: isSmallMobile ? 16 : 18 }}>{t.personalSettings}</Title>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Input defaultValue={user?.displayName || ''} addonBefore={t.name} size={isSmallMobile ? "small" : "middle"} style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: token.colorText, borderColor: 'rgba(255,255,255,0.18)' }} />
          <Input defaultValue={user?.email || ''} addonBefore={t.email} size={isSmallMobile ? "small" : "middle"} style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: token.colorText, borderColor: 'rgba(255,255,255,0.18)' }} />
          <Input addonBefore={t.address} size={isSmallMobile ? "small" : "middle"} style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: token.colorText, borderColor: 'rgba(255,255,255,0.18)' }} />
          <Input addonBefore={t.city} size={isSmallMobile ? "small" : "middle"} style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: token.colorText, borderColor: 'rgba(255,255,255,0.18)' }} />
          <Input addonBefore={t.zip} size={isSmallMobile ? "small" : "middle"} inputMode="numeric" style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: token.colorText, borderColor: 'rgba(255,255,255,0.18)' }} />
          <Input addonBefore={t.country} size={isSmallMobile ? "small" : "middle"} style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: token.colorText, borderColor: 'rgba(255,255,255,0.18)' }} />
          <Input addonBefore={t.state} size={isSmallMobile ? "small" : "middle"} style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: token.colorText, borderColor: 'rgba(255,255,255,0.18)' }} />
          <Input addonBefore={t.phone} size={isSmallMobile ? "small" : "middle"} inputMode="tel" style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: token.colorText, borderColor: 'rgba(255,255,255,0.18)' }} />
          <Button type="primary" loading={saveLoading} onClick={handleSaveSettings} size={isSmallMobile ? "small" : "middle"} block={isMobile}>{t.save}</Button>
        </Space>
      </Card>
    </motion.div>
  );

  const renderAccount = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <Card style={{ padding: isSmallMobile ? 12 : 16 }}>
        <Title level={4} style={{ marginTop: 0, color: token.colorText }}>{t.account}</Title>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Title level={5} style={{ margin: 0, color: token.colorText }}>{t.changePassword}</Title>

          <Input.Password
            value={pwdOld}
            onChange={e=>setPwdOld(e.target.value)}
            onFocus={lockZoom}
            onBlur={unlockZoom}
            placeholder={t.currentPassword}
            size={isSmallMobile ? 'small' : 'middle'}
            style={{ backgroundColor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.18)', fontSize: 16 }}
          />
          <Input.Password
            value={pwdNew}
            onChange={e=>setPwdNew(e.target.value)}
            onFocus={lockZoom}
            onBlur={unlockZoom}
            placeholder={t.newPassword}
            size={isSmallMobile ? 'small' : 'middle'}
            style={{ backgroundColor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.18)', fontSize: 16 }}
          />
          <Input.Password
            value={pwdNew2}
            onChange={e=>setPwdNew2(e.target.value)}
            onFocus={lockZoom}
            onBlur={unlockZoom}
            placeholder={t.confirmNewPassword}
            size={isSmallMobile ? 'small' : 'middle'}
            style={{ backgroundColor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.18)', fontSize: 16 }}
          />
          <Button type="primary" onClick={handleChangePassword} loading={pwdLoading} disabled={pwdLoading} block={isMobile}>
            {t.updatePassword}
          </Button>

          <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '8px 0' }} />

          <Title level={5} style={{ margin: 0, color: token.colorText }}>{t.deleteAccount}</Title>
          <Text style={{ color: token.colorTextSecondary }}>{t.deleteAccountDesc}</Text>
          <Button danger onClick={() => setDelOpen(true)} block={isMobile}>{t.deleteAccount}</Button>
        </Space>
      </Card>
    </motion.div>
  );

  const renderHelp = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <Card style={{ padding: isSmallMobile ? 12 : 16 }}>
        <Title level={4} style={{ marginTop: 0, color: token.colorText }}>{t.help}</Title>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text style={{ color: token.colorTextSecondary }}>{t.contactUs}</Text>
          <Button block={isMobile} href={`mailto:${APP_CONFIG.SUPPORT.email}`} type="default">
            {t.emailSupport}: {APP_CONFIG.SUPPORT.email}
          </Button>
          <Button block={isMobile} href={APP_CONFIG.SUPPORT.telegram} target="_blank" rel="noopener noreferrer" type="default">
            {t.telegramSupport}: @{APP_CONFIG.SUPPORT.telegram.replace(/^https?:\/\/t\.me\//, '')}
          </Button>
        </Space>
      </Card>
    </motion.div>
  );

  const headerLogoCenterStyle = { position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', display: 'flex', alignItems: 'center', pointerEvents: 'none', zIndex: 0 };
  const headerLogoImgStyle = { height: isSmallMobile ? 32 : 40, width: 'auto', opacity: 0.98 };

  return (
    <ConfigProvider
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          colorPrimary: APP_CONFIG.BRAND.primary,
          colorBgLayout: APP_CONFIG.BRAND.layoutBg,
          colorBgContainer: APP_CONFIG.BRAND.cardBg,
          colorBorderSecondary: 'rgba(255,255,255,0.08)',
          colorTextBase: '#EAF0F7',
          colorText: 'rgba(234,240,247,0.96)',
          colorTextSecondary: 'rgba(234,240,247,0.72)',
          colorTextPlaceholder: 'rgba(234,240,247,0.55)',
          colorLink: '#a9baff',
          colorLinkHover: '#c7d2fe',
          borderRadius: 12,
          wireframe: false,
          fontSize: isSmallMobile ? 12 : 14
        },
        components: {
          Layout: { headerBg: APP_CONFIG.BRAND.headerBg, bodyBg: APP_CONFIG.BRAND.layoutBg, siderBg: APP_CONFIG.BRAND.siderBg },
          Card: { colorBgContainer: APP_CONFIG.BRAND.cardBg, borderRadius: 12, padding: isSmallMobile ? 12 : 16 },
          Menu: {
            itemBorderRadius: 8, itemHeight: 44, itemMarginInline: 8, itemMarginBlock: 4,
            itemColor: 'rgba(234,240,247,0.88)', itemHoverColor: '#fff',
            itemHoverBg: 'rgba(108,140,255,0.10)', itemSelectedBg: 'rgba(108,140,255,0.18)', itemSelectedColor: '#fff', activeBarWidth: 0
          },
          Button: { borderRadius: 6 },
          Input: { borderRadius: 6, colorTextPlaceholder: 'rgba(234,240,247,0.55)' }
        }
      }}
    >
      <GlobalStyles />

      <Layout style={{ minHeight: '100vh', background: APP_CONFIG.BRAND.layoutBg }}>
        {!isMobile && (
          <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            theme="dark"
            width={240}
            breakpoint="lg"
            style={{ background: APP_CONFIG.BRAND.siderBg }}
          >
            <div style={logoStyle}>
              <img src="/logo-st.png" alt="logo" style={{ height: 28, width: 'auto', opacity: 0.95 }} />
              {!collapsed && (<span style={{ marginTop: 6, color: '#E6E9EF', fontWeight: 600, letterSpacing: 0.3, fontSize: 12 }}>Candy Bot Pro</span>)}
            </div>
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={[activeKey]}
              style={{ background: APP_CONFIG.BRAND.siderBg, borderRight: 0, paddingTop: 8 }}
              onClick={({ key }) => handleMenuClick(key)}
              items={[
                { key: 'dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
                { key: 'academy', icon: <ReadOutlined />, label: t.academy },
                { key: 'settings', icon: <SettingOutlined />, label: t.personalSettings },
                { key: 'account', icon: <SafetyCertificateFilled />, label: t.account },
                { key: 'help', icon: <QuestionCircleOutlined />, label: t.help },
                { type: 'divider' },
                 { key: 'refresh', icon: <ReloadOutlined />, label: t.refresh },
                { key: 'logout', icon: <LogoutOutlined />, label: <span style={{ color: '#ff6b6b' }}>{t.logout}</span>, danger: true }
              ]}
            />
          </Sider>
        )}

        <Layout>
          <Header style={isMobile ? mobileHeaderStyle : headerStyle}>
            <div style={headerLogoCenterStyle}><img src="/logo-st.png" alt="Logo" style={headerLogoImgStyle} /></div>
            <Space>
              {isMobile ? (
                <Button
                  type="text"
                  icon={<MenuOutlined style={{ fontSize: isSmallMobile ? 22 : 24 }} />}
                  onClick={() => setMobileMenuOpen(true)}
                  style={{ color: '#fff', width: 40, height: 40 }}
                />
              ) : (
                <Button
                  type="text"
                  icon={collapsed ? <MenuUnfoldOutlined style={{ fontSize: isSmallMobile ? 22 : 24 }} /> : <MenuFoldOutlined style={{ fontSize: isSmallMobile ? 22 : 24 }} />}
                  onClick={() => setCollapsed(!collapsed)}
                  style={{ color: token.colorText, width: 40, height: 40 }}
                />
              )}
            </Space>
            {!isMobile && (
              <Space>
                <Segmented size="small" options={[{ label: 'EN', value: 'en' }, { label: 'IT', value: 'it' }]} value={lang} onChange={setLang} />
              </Space>
            )}
          </Header>

          <Content style={isMobile ? mobileContentStyle : contentStyle}>
           <Spin spinning={loading} delay={300} size={isSmallMobile ? "small" : "default"}>
  <div key={`${activeKey}-${refreshTick}`}>
    {activeKey === 'dashboard' && renderDashboard()}
    {activeKey === 'academy' && renderAcademy()}
    {activeKey === 'settings' && renderSettings()}
    {activeKey === 'account' && renderAccount()}
    {activeKey === 'help' && renderHelp()}
  </div>
</Spin>

          </Content>
        </Layout>

        <Drawer
          title={
            <Space>
              <img src="/logo-st.png" alt="logo" style={{ height: 28, width: 'auto' }} />
              <Text strong style={{ color: token.colorText }}>𝒮𝓌𝑒𝑒𝓉 𝒯𝓇𝒶𝒹𝒾𝓃𝑔</Text>
            </Space>
          }
          placement="left"
          onClose={() => setMobileMenuOpen(false)}
          open={mobileMenuOpen}
          width={280}
          styles={{ body: { padding: '8px 0', background: APP_CONFIG.BRAND.siderBg }, header: { background: APP_CONFIG.BRAND.siderBg, borderBottom: '1px solid rgba(255,255,255,0.1)' } }}
        >
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[activeKey]}
            style={{ background: APP_CONFIG.BRAND.siderBg, borderRight: 0 }}
            onClick={({ key }) => handleMenuClick(key)}
            items={[
              { key: 'dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
              { key: 'academy', icon: <ReadOutlined />, label: t.academy },
              { key: 'settings', icon: <SettingOutlined />, label: t.personalSettings },
              { key: 'account', icon: <SafetyCertificateFilled />, label: t.account },
              { key: 'help', icon: <QuestionCircleOutlined />, label: t.help },
              { type: 'divider' },
              { key: 'logout', icon: <LogoutOutlined />, label: <span style={{ color: '#ff6b6b' }}>{t.logout}</span>, danger: true }
            ]}
          />
        </Drawer>

        {isMobile && (
          <FloatButton.Group shape="circle" style={{ right: 24, bottom: 24 }} icon={<MenuOutlined />} trigger="click">
            <FloatButton icon={<ReloadOutlined />} tooltip={lang === 'it' ? 'Aggiorna' : 'Refresh'} onClick={handleRefresh} />
          </FloatButton.Group>
        )}
      </Layout>

      {renderGuideModal()}

      <Modal
        open={delOpen}
        confirmLoading={delLoading}
        title={lang === 'it' ? 'Conferma cancellazione account' : 'Confirm account deletion'}
        okText={lang === 'it' ? 'Elimina' : 'Delete'}
        cancelText={lang === 'it' ? 'Annulla' : 'Cancel'}
        okButtonProps={{ danger: true }}
        onCancel={() => { setDelOpen(false); setDelConfirm(''); setDelPwd(''); }}
        onOk={async () => {
          if (delConfirm !== 'DELETE') {
            blackMsg(lang === 'it' ? 'Scrivi DELETE per confermare' : 'Type DELETE to confirm', 'error');
            return;
          }
          const auth = getAuth();
          const u = auth.currentUser;
          if (!u || !u.email) {
            blackMsg(lang === 'it' ? 'Utente non autenticato' : 'Not authenticated', 'error');
            return;
          }
          try {
            setDelLoading(true);
            const cred = EmailAuthProvider.credential(u.email, delPwd);
            await reauthenticateWithCredential(u, cred);
            await deleteUser(u);
            blackMsg(lang === 'it' ? 'Account eliminato' : 'Account deleted', 'success');
            await logout?.();
          } catch (e) {
            const msg = e?.code === 'auth/wrong-password'
              ? (lang === 'it' ? 'Password errata' : 'Wrong password')
              : e?.code === 'auth/requires-recent-login'
              ? (lang === 'it' ? 'Sessione scaduta, rieffettua login' : 'Session expired, sign in again')
              : (lang === 'it' ? 'Errore eliminazione' : 'Deletion failed');
            blackMsg(msg, 'error');
          } finally {
            setDelLoading(false);
          }
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            {lang === 'it'
              ? 'Azione irreversibile. Digita DELETE e inserisci la password:'
              : 'Irreversible action. Type DELETE and enter your password:'}
          </Text>
          <Input placeholder="DELETE" value={delConfirm} onChange={e=>setDelConfirm(e.target.value)} />
          <Input.Password
            placeholder={lang === 'it' ? 'Password attuale' : 'Current password'}
            value={delPwd}
            onChange={e=>setDelPwd(e.target.value)}
          />
        </Space>
      </Modal>
    </ConfigProvider>
  );
}

/* Styles */
const mobileHeaderStyle = {
  background: APP_CONFIG.BRAND.headerBg,
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 12px',
  height: 52,
  position: 'sticky',
  top: 0,
  zIndex: 100
};
const mobileContentStyle = { padding: 12, background: APP_CONFIG.BRAND.layoutBg, minHeight: 'calc(100vh - 52px)' };
const headerStyle = {
  background: APP_CONFIG.BRAND.headerBg,
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingInline: 16,
  position: 'sticky',
  top: 0,
  zIndex: 100,
  height: 64
};
const contentStyle = { padding: 16, background: APP_CONFIG.BRAND.layoutBg, minHeight: 'calc(100vh - 64px)' };
const logoStyle = {
  height: 64,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 0',
  borderBottom: '1px solid rgba(255,255,255,0.06)'
};
